import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  Check,
  CheckCircle,
  BarChart3,
  Brain,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  ChartColumnIncreasing,
  DatabaseBackup,
  MessageSquare,
  ArrowUp,
  CornerRightUp,
  Eye,
  EyeOff,
  ExternalLink,
  Hammer,
  History,
  Layers,
  Loader2,
  Minus,
  Plus,
  Quote,
  Search,
  Send,
  Sparkle,
  Sparkles,
  Star,
  Wrench,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { QueryProgress } from '../../hooks/useQueryStream';
import { cn } from '@/lib/utils';
import { useGlobalToast } from '@/components/UI/toaster';
import { displayTextFromStructuredPayload } from '@/utils/messageParser';
import { FeedbackModal } from './FeedbackModal';
import { ChatInput, AnalysisMode } from './ChatInput';
import { readPersistedAnalysisMode } from '../../utils/analysisMode';
import { financeFeedbackService } from '../../services/financeFeedbackService';
import { ChartData, DashboardCategoryGroup, DashboardData, KPIMetric } from '../../types/dashboard';
import { KPIGrid } from './KPIGrid';
import { ChartSection } from './ChartSection';
import { PerformanceLensSection } from './PerformanceLensSection';
import { ActionableInsightsSection } from './ActionableInsightsSection';
import type { ActionableInsightsBlockData } from '../../types/dashboard';
import { ValueAtStakeSection } from './ValueAtStakeSection';
import { NormalTextResponse } from './NormalTextResponse';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/UI/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { MarkdownContent } from '../ui/markdown-content';
import { repo2FinanceV2ClassNames } from '../../styles/repo2Brand';

interface StoredFeedback {
  rating: 'thumbs_up' | 'thumbs_down';
  text?: string | null;
  submitted_at?: string;
  submitted_by?: string;
}

export type InterruptOption = {
  action: string;
  label?: string;
  value?: string;
  description?: string;
  fields?: string[];
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  isCompleted?: boolean;  // Track if step is completed for green styling
  details?: string;
  icon?: React.ReactNode;
  nodeName?: string;  // Store node name for icon selection
  botMessageId?: string | null;  // Bot message ID to link dashboard data
  humanMessageId?: string | null;  // User message this response belongs to (keeps query-response pairing)
  dashboardData?: DashboardData | null;  // Dashboard data for this message
  /** Optional follow-up suggestions from normal_text responses */
  suggestedQueries?: string[];
  /** Explicit LangGraph interrupt actions the user can choose. */
  interruptOptions?: InterruptOption[];
  /** Optional status from backend (e.g. insufficient_data) */
  status?: string;
  /** Backend error code, when an assistant response is an error payload. */
  errorCode?: string | null;
  /** Persisted feedback (rating + text) — undefined when not given yet. */
  feedback?: StoredFeedback | null;
}

// Use the ConversationMessage from conversationService but extend it
type ConversationMessage = {
  id?: string;
  messageId?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string | number | Date;
  botMessageId?: string | null;
  dashboardData?: DashboardData | null;  // Dashboard data for this message/query
  humanMessageId?: string | null;
  isLiveQuery?: boolean;
  message?: string;
  normal_text?: string;
  suggestedQueries?: string[];
  interruptOptions?: InterruptOption[];
  feedback?: StoredFeedback | null;
  status?: string;
  errorCode?: string | null;
  error_code?: string | null;
}

interface ChatInterfaceProps {
  userQuery?: string;
  progress: QueryProgress;
  isLoading: boolean;
  welcomeQuestions: string[];
  /** SSE stream is currently open. Stays true through final → saved →
   *  complete; flips to false only on onclose / error. Used to keep
   *  the live trace card mounted until the stream physically closes,
   *  not the moment a partial dashboard becomes available. */
  isConnected?: boolean;
  /** Active chat-v2 thread id (UUID). Required for feedback submission. */
  threadId?: string | null;
  botMessageId?: string | null;  // Bot message ID from Communication Infrastructure for feedback
  conversationMessages?: ConversationMessage[];  // All conversation messages from history
  dashboardDataMap?: Map<string, DashboardData>;  // Map of dashboard data by botMessageId/query
  currentDashboardData?: DashboardData | null;  // Current dashboard data being loaded
  /** Data source from backend for loaded conversation history. */
  conversationDataSourceName?: string;
  /** Current-session data source. */
  activeDataSourceName?: string;
  /** Trigger a new query when user clicks a suggested question (same pipeline & conversation) */
  onSuggestedQueryClick?: (query: string) => void;
  /** Composer submit handler. Optional so existing consumers do not break. */
  onSubmit?: (query: string, analysisMode: AnalysisMode) => void;
  /** Explicit LangGraph interrupt action handler. */
  onInterruptAction?: (action: string, label?: string, value?: string) => void;
  onExport?: (format: 'ppt' | 'pdf') => void;
  onDataSourceClick?: () => void;
  onConnectDataSourceClick?: (context?: {
    threadId?: string | null;
    retryQuery?: string | null;
    reconnectActionLabel?: string | null;
    reconnectErrorMessage?: string | null;
    reconnectErrorMessageId?: string | null;
    reconnectErrorCode?: string | null;
  }) => void | Promise<void>;
  onDisconnectDataSourceClick?: () => void | Promise<void>;
  activeDataSource?: { name: string; type: string; datasourceId?: string } | null;
  hasLoadedActiveDataSource?: boolean;
  dataSourceConnectionResult?: 'connected' | 'error' | 'disconnected' | null;
  setupContextResetKey?: number;
  isDisconnectingDataSource?: boolean;
  isNewUser?: boolean | null;
  userDisplayName?: string;
  onBookmarksClick?: () => void | Promise<void>;
  onHistoryClick?: () => void;
  onNewChatClick?: () => void;
  isHistoryOpen?: boolean;
  panelTitle?: string;
  panelSubtitle?: string;
  /** Open the data-lineage drawer for a KPI metric. */
  onShowKpiLineage?: (metric: KPIMetric) => void;
  /** Open the same data-lineage drawer for a chart. Wired to the
   *  chart info icon — shows the same "What this is / Values / OData
   *  References" UX as KPIs. */
  onShowChartLineage?: (chart: ChartData) => void;
}

// Loading skeleton component for sections that are still loading
const SectionLoadingSkeleton = ({ title, icon: Icon }: { title: string; icon: React.ComponentType<{ className?: string }> }) => (
  <div className={cn(repo2FinanceV2ClassNames.card, "p-6 animate-pulse")}>
    <div className="flex items-center gap-3 mb-4">
      <div className="rounded-[10px] bg-[#f5e9ee] p-2">
        <Icon className="h-5 w-5 text-[#6E3450]/60" />
      </div>
      <div className="h-5 w-32 rounded bg-[#EDE5D8]"></div>
    </div>
    <div className="space-y-3">
      <div className="h-4 w-full rounded bg-[#F5EFE6]"></div>
      <div className="h-4 w-3/4 rounded bg-[#F5EFE6]"></div>
      <div className="h-4 w-1/2 rounded bg-[#F5EFE6]"></div>
    </div>
    <div className="mt-4 flex items-center gap-2 text-sm text-[#7B6B72]">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#6E3450]/45 border-t-transparent" />
      <span>{title}</span>
    </div>
  </div>
);

const InlineLoadingSkeleton = ({ title }: { title: string }) => (
  <div className="animate-pulse rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1]/80 p-4">
    <div className="space-y-3">
      <div className="h-4 w-full rounded bg-[#EDE5D8]" />
      <div className="h-4 w-4/5 rounded bg-[#EDE5D8]" />
      <div className="h-4 w-3/5 rounded bg-[#EDE5D8]" />
    </div>
    <div className="mt-3 flex items-center gap-2 text-xs text-[#7B6B72]">
      <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#6E3450]/45 border-t-transparent" />
      <span>{title}</span>
    </div>
  </div>
);

const DashboardSummaryBlock = ({ content }: { content?: string | null }) => {
  const text = content?.trim();
  if (!text) return null;

  return (
    <section className="rounded-[14px] border border-[#EDE6E1] bg-white px-6 py-5 shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
      <MarkdownContent
        content={text}
        className="font-display text-[#3F3338] [&_blockquote]:border-[#C9A24E]/60 [&_blockquote]:bg-[#FAF7F1] [&_blockquote]:text-[#3F3338] [&_code]:border [&_code]:border-[#EDE6E1] [&_code]:bg-[#FAF7F1] [&_code]:text-[#3F3338] [&_h1]:text-[#1F1A20] [&_h2]:text-[#1F1A20] [&_h3]:text-[#1F1A20] [&_li]:text-[15px] [&_li]:leading-7 [&_p]:my-2 [&_p]:text-[15px] [&_p]:leading-7 [&_p]:text-[#3F3338] [&_strong]:text-[#1F1A20]"
      />
    </section>
  );
};

type ReasoningTraceSectionModel = {
  key: string;
  title?: string;
  helperText?: string;
  summaryContent?: string;
  blockType?: string;
  structuredData?: Record<string, unknown>;
  group?: DashboardCategoryGroup;
};

const isReasoningTraceText = (value?: string | null) => /reasoning[\s_-]*trace/i.test(value || '');
const isMethodologyText = (value?: string | null) => /methodolog/i.test(value || '');

const isReasoningTraceSection = (section: ReasoningTraceSectionModel): boolean => (
  isReasoningTraceText(section.blockType) ||
  isReasoningTraceText(section.group?.blockType) ||
  isReasoningTraceText(section.title) ||
  isReasoningTraceText(section.group?.title) ||
  isReasoningTraceText(section.group?.category) ||
  isMethodologyText(section.blockType) ||
  isMethodologyText(section.group?.blockType) ||
  isMethodologyText(section.title) ||
  isMethodologyText(section.group?.title) ||
  isMethodologyText(section.group?.category)
);

// Repo-2 branding pass: render v2 lens blocks as structured columns instead of markdown bullets.
const isLensSection = (section: ReasoningTraceSectionModel): boolean => {
  const values = [
    section.blockType,
    section.group?.blockType,
    section.title,
    section.group?.title,
    section.group?.category,
  ].map((value) => String(value || '').toLowerCase());
  return values.some((value) => /\blens\b/.test(value) || /performance\s+lens/.test(value));
};

const lensSectionData = (section: ReasoningTraceSectionModel): Record<string, unknown> | null => {
  const data = section.structuredData || section.group?.structuredData;
  return data && typeof data === 'object' ? data : null;
};

// Repo-2 reference: render the Insights block as lane-titled prose
// (HEADLINE / CONCENTRATION / VARIANCE DRIVERS / TREND / CROSS DIM)
// rather than markdown bullets, so the curator's analyst prose
// surfaces with the faded uppercase lane labels the design calls for.
const isInsightsSection = (section: ReasoningTraceSectionModel): boolean => {
  const values = [
    section.blockType,
    section.group?.blockType,
    section.title,
    section.group?.title,
    section.group?.category,
  ].map((value) => String(value || '').toLowerCase());
  return values.some((value) => value === 'insights' || value === 'insight');
};

const insightsSectionData = (section: ReasoningTraceSectionModel): Record<string, unknown> | null => {
  const data = section.structuredData || section.group?.structuredData;
  return data && typeof data === 'object' ? data : null;
};

// Actionable Insights block — the reasoning layer that converts
// descriptive surfaces into prescriptive recommendations. Detect by
// blockType / category / structuredData shape so the canvas renders
// the dedicated ActionableInsightsSection component (priority chips,
// evidence disclosure, related-KPI/Chart buttons) instead of falling
// back to the generic summary block.
const isActionableInsightsSection = (section: ReasoningTraceSectionModel): boolean => {
  const values = [
    section.blockType,
    section.group?.blockType,
    section.title,
    section.group?.title,
    section.group?.category,
  ].map((value) => String(value || '').toLowerCase());
  if (values.some((value) => value === 'actionable_insights' || value === 'actionable insights')) {
    return true;
  }
  const data = section.structuredData || section.group?.structuredData;
  const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  if (record && Array.isArray(record.items) && record.items.length > 0) {
    const first = record.items[0] as Record<string, unknown> | undefined;
    if (first && typeof first === 'object' && 'priority' in first && 'recommended_action' in first) {
      return true;
    }
  }
  return false;
};

const actionableInsightsSectionData = (
  section: ReasoningTraceSectionModel,
): ActionableInsightsBlockData | null => {
  const data = section.structuredData || section.group?.structuredData;
  if (!data || typeof data !== 'object') return null;
  return data as unknown as ActionableInsightsBlockData;
};

function InsightsLanesSection({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return null;
  const stringField = (k: string): string => {
    const v = (data as Record<string, unknown>)[k];
    return typeof v === 'string' ? v.trim() : '';
  };

  // Lanes are now Markdown-authored by the backend's
  // ``insight_narrator`` node. We pass each lane's content
  // through ``MarkdownContent`` so the LLM can use headings,
  // lists, emphasis, and tables in the prose without us
  // post-processing it on the FE.
  const lanes: Array<{ label: string; text: string }> = [
    { label: 'HEADLINE',         text: stringField('headline') },
    { label: 'CONCENTRATION',    text: stringField('concentration_prose') },
    { label: 'VARIANCE DRIVERS', text: stringField('variance_drivers_prose') },
    { label: 'TREND',            text: stringField('trend_prose') },
    { label: 'CROSS DIM',        text: stringField('cross_dim_prose') },
  ].filter((lane) => lane.text.length > 0);

  if (lanes.length === 0) return null;

  return (
    <div className="rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1]/70 p-5 space-y-5">
      {lanes.map((lane) => (
        <div key={lane.label} className="space-y-2">
          <div className="font-display text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B7A82]">
            {lane.label}
          </div>
          <MarkdownContent
            content={lane.text}
            className="text-[14px] leading-relaxed text-[#3F3338]"
          />
        </div>
      ))}
    </div>
  );
}

// Repo-2 reference: render value_at_stake payloads as structured opportunity cards, not markdown bullets.
const isValueAtStakeSection = (section: ReasoningTraceSectionModel): boolean => {
  const data = section.structuredData || section.group?.structuredData;
  const values = [
    section.blockType,
    section.group?.blockType,
    section.title,
    section.group?.title,
    section.group?.category,
  ].map((value) => String(value || '').toLowerCase());
  const recordData = data && typeof data === 'object' ? data as Record<string, unknown> : null;
  const itemRecords = Array.isArray(recordData?.items)
    ? recordData.items.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object' && !Array.isArray(item)))
    : [];

  return values.some((value) => /value[\s_-]*at[\s_-]*stake/.test(value) || /dollars[\s_-]*at[\s_-]*stake/.test(value)) ||
    Boolean(recordData && (
      recordData.total_low_usd !== undefined ||
      recordData.total_high_usd !== undefined ||
      itemRecords.some((item) => (
        item.low_usd !== undefined ||
        item.high_usd !== undefined ||
        item.usd_at_stake !== undefined ||
        item.usd_value !== undefined
      ))
    ));
};

const valueAtStakeSectionData = (section: ReasoningTraceSectionModel): Record<string, unknown> | null => {
  const data = section.structuredData || section.group?.structuredData;
  return data && typeof data === 'object' ? data : null;
};

type NextActionPrompt = {
  id: string;
  query: string;
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value && typeof value === 'object' && !Array.isArray(value))
);

const isNextActionsSection = (section: ReasoningTraceSectionModel): boolean => {
  const data = section.structuredData || section.group?.structuredData;
  const values = [
    section.blockType,
    section.group?.blockType,
    section.title,
    section.group?.title,
    section.group?.category,
  ].map((value) => String(value || '').toLowerCase());

  return values.some((value) => /next[\s_-]*actions?/.test(value)) ||
    Boolean(isPlainRecord(data) && Array.isArray(data.actions));
};

const firstDisplayString = (...values: unknown[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const nextActionPromptsFromSection = (section: ReasoningTraceSectionModel): NextActionPrompt[] => {
  const data = section.structuredData || section.group?.structuredData;
  if (!isPlainRecord(data) || !Array.isArray(data.actions)) return [];

  return data.actions
    .map((action, index) => {
      const record = isPlainRecord(action) ? action : {};
      const query = firstDisplayString(
        record.query,
        record.prompt,
        record.question,
        record.label,
        record.title,
      );
      if (!query) return null;

      return {
        id: firstDisplayString(record.id, `${section.key}-next-action-${index}`) || `${section.key}-next-action-${index}`,
        query,
      };
    })
    .filter((action): action is NextActionPrompt => Boolean(action));
};

const formatDynamicLabel = (value: unknown): string => {
  const text = firstDisplayString(value);
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isEmptyDynamicValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyDynamicValue);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every(isEmptyDynamicValue);
  return false;
};

const looksLikeFieldToken = (value: string): boolean => (
  /^[A-Za-z][A-Za-z0-9_.$]*$/.test(value) && /[_.$]|\b[A-Z]{2,}\b/.test(value)
);

type PointSummaryItem = {
  label?: string;
  body: string;
};

const stripMarkdownMarkers = (value: string): string => (
  value
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .trim()
);

const parsePointSummaryText = (content?: string | null): PointSummaryItem[] => {
  const normalized = String(content || '')
    .replace(/\\n/g, '\n')
    .replace(/\/\/n/g, '\n')
    .replace(/\/n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const bullets: string[] = [];
  normalized.split('\n').forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;
    const bulletMatch = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1].trim());
    } else if (bullets.length > 0) {
      bullets[bullets.length - 1] = `${bullets[bullets.length - 1]} ${line}`.trim();
    }
  });

  return bullets.map((rawItem) => {
    const item = rawItem.trim();
    const boldLabelMatch = item.match(/^\*\*([^:*][^:]{0,78}):\*\*\s*(.+)$/);
    const plainLabelMatch = item.match(/^([^:\n]{1,80}):\s+(.+)$/);
    const match = boldLabelMatch || plainLabelMatch;
    if (!match) return { body: stripMarkdownMarkers(item) };

    return {
      label: formatDynamicLabel(stripMarkdownMarkers(match[1])),
      body: stripMarkdownMarkers(match[2]),
    };
  }).filter((item) => item.body);
};

const pointSummarySectionText = (section: ReasoningTraceSectionModel): string => [
  section.blockType,
  section.group?.blockType,
  section.title,
  section.group?.title,
  section.group?.category,
].map((value) => String(value || '').toLowerCase()).join(' ');

const isPointSummarySection = (section: ReasoningTraceSectionModel): boolean => {
  const text = pointSummarySectionText(section);
  return /insights?/.test(text) || /what[\s_-]*if/.test(text) || /\bscenario\b/.test(text);
};

const isScenarioPointSection = (section: ReasoningTraceSectionModel): boolean => {
  const text = pointSummarySectionText(section);
  return /what[\s_-]*if/.test(text) || /\bscenario\b/.test(text);
};

const shouldSpanPointSummaryItem = (item: PointSummaryItem): boolean => (
  item.body.length > 150 || item.body.split(/\s+/).length > 22
);

const renderPointSummaryBody = (body: string) => {
  const commaItems = body.split(',').map((item) => item.trim()).filter(Boolean);
  const semicolonItems = body.split(';').map((item) => item.trim()).filter(Boolean);
  const listItems = commaItems.length > 1 ? commaItems : semicolonItems;
  const useCodeChips = listItems.length > 1 && listItems.every(looksLikeFieldToken);

  if (useCodeChips) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {listItems.map((item) => (
          <code
            key={item}
            className="rounded-[6px] border border-[#EDE6E1] bg-white px-2.5 py-1 font-mono text-[12.5px] leading-none text-[#3F3338]"
          >
            {item}
          </code>
        ))}
      </div>
    );
  }

  return (
    <MarkdownContent
      content={body}
      className="font-display text-[14px] leading-6 text-[#1F1A20] [&_p]:my-0 [&_p]:text-[14px] [&_p]:leading-6 [&_p]:text-[#1F1A20] [&_strong]:font-semibold [&_strong]:text-[#1F1A20]"
    />
  );
};

const PointSummaryBlock = ({ content }: { content?: string | null }) => {
  const items = parsePointSummaryText(content);
  if (items.length === 0) return <DashboardSummaryBlock content={content} />;

  return (
    <section className="rounded-[14px] border border-[#EDE6E1] bg-white px-5 py-4 shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
      <div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
        {items.map((item, index) => (
          <div
            key={`${item.label || 'point'}-${index}`}
            className={cn(
              "min-w-0",
              shouldSpanPointSummaryItem(item) && "md:col-span-2"
            )}
          >
            {item.label && (
              <div className="mb-1 font-display text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
                {item.label}
              </div>
            )}
            {renderPointSummaryBody(item.body)}
          </div>
        ))}
      </div>
    </section>
  );
};

const NextActionsPromptBlock = ({
  section,
  onSuggestedQueryClick,
}: {
  section: ReasoningTraceSectionModel;
  onSuggestedQueryClick?: (query: string) => void;
}) => {
  const actions = nextActionPromptsFromSection(section);
  if (actions.length === 0) return <DashboardSummaryBlock content={section.summaryContent} />;

  return (
    <section className="grid gap-3">
      {actions.map((action) => {
        const content = (
          <>
            <span className="min-w-0 flex-1 text-left font-display text-[14px] leading-6 text-[#1F1A20]">
              {action.query}
            </span>
            {onSuggestedQueryClick && (
              <Send className="h-4 w-4 shrink-0 text-[#6E3450]" aria-hidden="true" />
            )}
          </>
        );

        return onSuggestedQueryClick ? (
          <button
            key={action.id}
            type="button"
            onClick={() => onSuggestedQueryClick(action.query)}
            className="flex min-w-0 items-center gap-3 rounded-[10px] border border-[#EDE5D8] bg-white px-4 py-3.5 text-left shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] transition-colors hover:border-[#6E3450]/40 hover:bg-[#f5e9ee]/60"
            title="Click to ask this question with the same data source"
          >
            {content}
          </button>
        ) : (
          <div
            key={action.id}
            className="flex min-w-0 items-center gap-3 rounded-[10px] border border-[#EDE5D8] bg-white px-4 py-3.5 shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]"
          >
            {content}
          </div>
        );
      })}
    </section>
  );
};

const DashboardSectionSummaryBlock = ({
  section,
  onSuggestedQueryClick,
}: {
  section: ReasoningTraceSectionModel;
  onSuggestedQueryClick?: (query: string) => void;
}) => {
  if (isNextActionsSection(section)) {
    return (
      <NextActionsPromptBlock
        section={section}
        onSuggestedQueryClick={onSuggestedQueryClick}
      />
    );
  }

  return isPointSummarySection(section) || isScenarioPointSection(section)
    ? <PointSummaryBlock content={section.summaryContent} />
    : <DashboardSummaryBlock content={section.summaryContent} />;
};

const formatDynamicScalar = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  return firstDisplayString(value);
};

const renderDynamicTraceValue = (value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    const items = value.filter((item) => !isEmptyDynamicValue(item));
    if (items.length === 0) return null;

    if (items.every((item) => typeof item !== 'object' || item === null)) {
      const values = items.map(formatDynamicScalar).filter(Boolean);
      const useCodeChips = values.length > 0 && values.every(looksLikeFieldToken);
      if (useCodeChips) {
        return (
          <div className="flex flex-wrap gap-1.5">
            {values.map((item) => (
              <code
                key={item}
                className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1] px-2 py-1 font-mono text-[12.5px] leading-none text-[#3F3338]"
              >
                {item}
              </code>
            ))}
          </div>
        );
      }
      return <div>{values.join(' · ')}</div>;
    }

    return (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-[8px] border border-[#EDE6E1] bg-[#FAF7F1]/70 p-3">
            {renderDynamicTraceValue(item)}
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, nestedValue]) => !isEmptyDynamicValue(nestedValue));
    if (entries.length === 0) return null;

    return (
      <div className="space-y-2">
        {entries.map(([nestedKey, nestedValue]) => (
          <div key={nestedKey} className="grid gap-1 sm:grid-cols-[minmax(112px,0.34fr)_minmax(0,1fr)]">
            <div className="font-display text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B7A82]">
              {formatDynamicLabel(nestedKey)}
            </div>
            <div className="min-w-0">{renderDynamicTraceValue(nestedValue)}</div>
          </div>
        ))}
      </div>
    );
  }

  return formatDynamicScalar(value);
};

const shouldSpanDynamicTraceRow = (value: unknown): boolean => (
  Boolean(value && typeof value === 'object') ||
  (Array.isArray(value) && value.length > 2) ||
  (typeof value === 'string' && value.length > 150)
);

const ReasoningTraceRow = ({
  label,
  children,
  span = false,
}: {
  label: string;
  children: React.ReactNode;
  span?: boolean;
}) => (
  <div className={cn("min-w-0", span && "md:col-span-2")}>
    <div className="mb-1 font-display text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#8B7A82]">
      {label}
    </div>
    <div className="min-w-0 font-display text-[14px] leading-6 text-[#1F1A20] [overflow-wrap:anywhere]">
      {children}
    </div>
  </div>
);

const ReasoningTraceAccordionSection = ({ section }: { section: ReasoningTraceSectionModel }) => {
  const data = (section.structuredData || section.group?.structuredData || {}) as Record<string, unknown>;
  const title = formatDynamicLabel(firstDisplayString(
    section.title,
    section.group?.title,
    section.blockType,
    section.group?.blockType,
    section.group?.category
  )) || formatDynamicLabel(section.key);
  const subtitle = firstDisplayString(section.helperText, section.group?.description);
  const fallbackContent = section.summaryContent || section.group?.summary || '';
  const entries = Object.entries(data).filter(([, value]) => !isEmptyDynamicValue(value));

  return (
    <AccordionItem
      key={section.key}
      value={section.key}
      className="overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]"
    >
      <AccordionTrigger className="border-b border-[#EDE6E1] px-6 py-4 hover:no-underline [&>svg]:text-[#7B6B72]">
        <div className="flex min-w-0 items-center gap-3 text-left">
          <BrainCircuit className="h-4 w-4 shrink-0 text-[#1F1A20]" />
          <span className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-[#7B6B72]">
            {title}
          </span>
          {subtitle && (
            <span className="truncate font-display text-[14px] italic text-[#7B6B72]">
              {subtitle}
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-5 pb-5 pt-4">
        {entries.length > 0 ? (
          <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
            {entries.map(([key, value]) => (
              <ReasoningTraceRow
                key={key}
                label={formatDynamicLabel(key)}
                span={shouldSpanDynamicTraceRow(value)}
              >
                {renderDynamicTraceValue(value)}
              </ReasoningTraceRow>
            ))}
          </div>
        ) : (
          <DashboardSummaryBlock content={fallbackContent} />
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

type DashboardRenderableSectionModel = ReasoningTraceSectionModel & {
  metrics?: KPIMetric[];
  charts?: DashboardData['charts'];
};

const DashboardSectionHeaderContent = ({
  title,
  helperText,
}: {
  title?: string;
  helperText?: string;
}) => (
  <div className="flex w-full items-start justify-between gap-4 text-left">
    <div className="min-w-0">
      <div className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-[#7B6B72]">
        {title}
      </div>
      {helperText && (
        <div className="pt-1.5 font-display text-[14px] leading-relaxed text-[#8B7A82]">
          {helperText}
        </div>
      )}
    </div>
  </div>
);

const DashboardAccordionCard = ({
  value,
  title,
  helperText,
  children,
}: {
  value: string;
  title?: string;
  helperText?: string;
  children: React.ReactNode;
}) => (
  <AccordionItem
    value={value}
    className="min-w-0 overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]"
  >
    <AccordionTrigger className="border-b border-[#EDE6E1] px-6 py-4 hover:no-underline [&>svg]:text-[#7B6B72]">
      <DashboardSectionHeaderContent title={title} helperText={helperText} />
    </AccordionTrigger>
    <AccordionContent className="px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
      {children}
    </AccordionContent>
  </AccordionItem>
);

const DashboardStaticSectionCard = ({
  title,
  helperText,
  children,
}: {
  title?: string;
  helperText?: string;
  children: React.ReactNode;
}) => (
  <section className="min-w-0 overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
    <header className="border-b border-[#EDE6E1] px-6 py-4">
      <DashboardSectionHeaderContent title={title} helperText={helperText} />
    </header>
    <div className="px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
      {children}
    </div>
  </section>
);

const isDashboardHeadlineSection = (section: DashboardRenderableSectionModel): boolean => {
  const category = section.group?.category || section.key.replace(/^group-/, '');
  const labels = [
    section.title,
    section.group?.title,
    section.blockType,
    section.group?.blockType,
    section.group?.category,
  ].map((value) => String(value || '').trim().toLowerCase());
  const metrics = section.metrics ?? section.group?.metrics ?? [];
  const charts = section.charts ?? section.group?.charts ?? [];
  const hasVisuals = metrics.length > 0 || charts.length > 0;
  const hasSummary = Boolean((section.summaryContent || section.group?.summary || '').trim());

  return labels.some((value) => /\bheadline\b/.test(value)) ||
    (category.trim().toLowerCase() === 'other' && hasSummary && !hasVisuals);
};

const formatDashboardCategoryTitle = (category?: string | null) => {
  const text = category?.trim();
  if (!text) return '';
  return text.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const resolveDashboardSectionTitle = (group: { category?: string; title?: string; summary?: string }) => {
  const explicitTitle = group.title?.trim();
  if (explicitTitle) return explicitTitle;

  const category = group.category?.trim();
  if (category && category.toLowerCase() !== 'other') {
    return formatDashboardCategoryTitle(category);
  }

  const firstSummaryLine = group.summary?.trim().split(/\n|[.!?]\s+/)[0]?.trim();
  return firstSummaryLine || 'Overview';
};

const resolveDashboardSectionHelperText = (
  group: { description?: string; structuredData?: Record<string, unknown> },
  itemCount: number,
  dashboardIsLoading: boolean,
  hasSectionContent = false
) => {
  const explicitDescription = group.description?.trim();
  const sectionHasLoadedContent = hasSectionContent || !isEmptyDynamicValue(group.structuredData);
  if (explicitDescription) return explicitDescription;
  if (itemCount > 0) return `${itemCount} visual${itemCount === 1 ? '' : 's'} in this section`;
  if (sectionHasLoadedContent) return '';
  return dashboardIsLoading ? 'Loading visuals...' : '';
};

// Icon mapping for different node types - simple icons
const getNodeIcon = (nodeName: string, isCompleted: boolean = false) => {
  const iconClass = isCompleted ? "h-5 w-5 stroke-[2.5]" : "h-4 w-4";

  // Analysis and planning operations
  if (nodeName.includes('query') || nodeName.includes('Understanding') || nodeName.includes('Finding') ||
    nodeName.includes('sql_plan') || nodeName.includes('Planning') || nodeName.includes('financial_analyst') ||
    nodeName.includes('Analyzing') || nodeName.includes('intelligence')) {
    return <Brain className={iconClass} />;
  }
  // Charts and visualization
  if (nodeName.includes('chart') || nodeName.includes('Designing') || nodeName.includes('Creating')) {
    return <BarChart3 className={iconClass} />;
  }
  // Summary and intelligence
  if (nodeName.includes('analytical_summary') || nodeName.includes('Summarizing') || nodeName.includes('Generating') ||
    nodeName.includes('computation') || nodeName.includes('Computing')) {
    return <CheckCircle className={iconClass} />;
  }
  // Default
  return <Brain className={iconClass} />;
};

// Typing dots animation component
const TypingDots = () => {
  return (
    <span className="inline-flex items-center gap-1 ml-1">
      <motion.span
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.4, repeat: Infinity, delay: 0 }}
        className="inline-block w-1 h-1 rounded-full bg-current"
      />
      <motion.span
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
        className="inline-block w-1 h-1 rounded-full bg-current"
      />
      <motion.span
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.4, repeat: Infinity, delay: 0.4 }}
        className="inline-block w-1 h-1 rounded-full bg-current"
      />
    </span>
  );
};

const PanelActionButton = ({
  title,
  onClick,
  icon,
  active = false,
  disabled = false,
}: {
  title: string;
  onClick?: () => void;
  icon: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={active || undefined}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-[#7B6B72] transition-colors duration-200",
      active
        ? "bg-[#f5e9ee] text-[#6E3450]"
        : "hover:bg-[#FAF7F1] hover:text-[#1F1A20]",
      disabled && "cursor-not-allowed opacity-45 hover:bg-transparent hover:text-[#7B6B72]"
    )}
  >
    {icon}
  </button>
);

const extractBackendErrorCode = (message?: string | null): string | null => {
  if (!message) return null;
  const explicitMatch = message.match(/Error Code:\s*([A-Z0-9_]+)/i);
  if (explicitMatch?.[1]) return explicitMatch[1].toUpperCase();

  const jsonMatch = message.match(/["']error_code["']\s*:\s*["']([A-Z0-9_]+)["']/i);
  if (jsonMatch?.[1]) return jsonMatch[1].toUpperCase();

  const sapCodeMatch = message.match(/\b(SAP_(?:NOT_CONNECTED|REFRESH_FAILED|REFRESH_TOKEN_EXPIRED|TOKEN_EXPIRED|UNAVAILABLE))\b/i);
  return sapCodeMatch?.[1]?.toUpperCase() ?? null;
};

const normalizeBackendErrorCode = (value?: string | null): string | null => {
  const code = value?.trim().toUpperCase();
  return code || null;
};

const SAP_RECONNECT_ERROR_CODES = new Set([
  'SAP_NOT_CONNECTED',
  'SAP_REFRESH_FAILED',
  'SAP_REFRESH_TOKEN_EXPIRED',
  'SAP_TOKEN_EXPIRED',
]);

const isSapReconnectErrorCode = (code?: string | null): boolean => {
  const normalized = normalizeBackendErrorCode(code);
  return Boolean(normalized && SAP_RECONNECT_ERROR_CODES.has(normalized));
};

const stripBackendErrorCode = (message: string): string => (
  message.replace(/\n{0,2}Error Code:\s*[A-Z0-9_]+/i, '').trim()
);

const CHAT_FEEDBACK_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_CHAT_FEEDBACK_TEXT_LENGTH = 500;

const getPersistedFeedbackMessageId = (
  message: Pick<ChatMessage, 'id' | 'botMessageId'>,
): string | null => {
  const candidate = message.botMessageId || message.id;
  return candidate && CHAT_FEEDBACK_UUID_RE.test(candidate) ? candidate : null;
};

// Repo-2 branding pass: keep backend progress data, but translate node labels
// into the concise, user-facing trace language used by repo-2 chat cards.
const REPO2_TRACE_COPY_BY_NODE: Record<string, string> = {
  intake: 'Understanding your question',
  analyst_intake: 'Understanding your question',
  catalog: 'Reviewing available data',
  table_selection: 'Selecting the relevant tables',
  analytical_column_selection: 'Choosing the right columns',
  get_schema: 'Reading table schemas',
  prepare_analytical_schema: 'Preparing the analysis schema',
  sql_plan: 'Planning the query',
  generate_sql: 'Generating the query',
  sap_fetch_plan: 'Planning SAP data fetches',
  sap_data_fetch_simple: 'Fetching SAP data',
  fetch_data: 'Fetching the source data',
  load_data: 'Loading the source data',
  analytical_fetch_plan: 'Planning analytical fetches',
  financial_analyst_planner: 'Planning the analysis path',
  computation_engine: 'Running the calculations',
  analytical_summary: 'Writing the summary',
  intelligence_analysis: 'Checking business drivers',
  chart_preplan: 'Planning the dashboard layout',
  chart_preparation: 'Building the charts',
  post_charts_node: 'Finalizing the charts',
  response_orchestration: 'Assembling the response',
  revenue_margin_actuals: 'Pulling revenue and margin actuals',
  plan_vs_actual: 'Comparing actuals against plan',
  project_period_revenue_margin: 'Projecting period revenue and margin',
  top_drivers: 'Identifying the top drivers',
  lookup_data_dictionary: 'Looking up the data dictionary',
};

const REPO2_TRACE_COPY_BY_LABEL: Record<string, string> = {
  'Understanding the Question': 'Understanding your question',
  'Browsing the Data Catalog': 'Reviewing available data',
  'Selecting Relevant Tables': 'Selecting the relevant tables',
  'Picking the Right Columns': 'Choosing the right columns',
  'Reading Table Schemas': 'Reading table schemas',
  'Preparing the Schema': 'Preparing the analysis schema',
  'Planning the Queries': 'Planning the query',
  'Generating Queries': 'Generating the query',
  'Planning Analytical Fetches': 'Planning analytical fetches',
  'Planning the Analysis': 'Planning the analysis path',
  'Running Calculations': 'Running the calculations',
  'Writing the Summary': 'Writing the summary',
  'Drilling Into Drivers': 'Checking business drivers',
  'Planning the Visualization': 'Planning the dashboard layout',
  'Building Charts': 'Building the charts',
  'Finalizing Charts': 'Finalizing the charts',
  'Assembling the Response': 'Assembling the response',
};

const cleanTraceText = (value?: string) => {
  const raw = value?.trim();
  if (!raw) return '';
  if (/^consulting\s+\S+/i.test(raw)) return 'Working through your question';
  return raw
    .replace(/\s*\(turn\s*\d+\)\s*/gi, '')
    .replace(/\s+processed successfully$/i, '')
    .replace(/\s+completed$/i, '')
    .trim();
};

const formatTraceLabel = (value?: string, nodeName?: string) => {
  const cleaned = cleanTraceText(value);
  const nodeCopy = nodeName ? REPO2_TRACE_COPY_BY_NODE[nodeName] : '';
  const labelCopy = REPO2_TRACE_COPY_BY_LABEL[cleaned];
  const normalizedCleaned = cleaned.toLowerCase();
  const normalizedNodeName = (nodeName || '').replace(/_/g, ' ').toLowerCase();
  const isNodeEcho = !cleaned
    || normalizedCleaned === normalizedNodeName
    || normalizedCleaned === (nodeName || '').toLowerCase()
    || Boolean(nodeCopy && (cleaned === nodeCopy || labelCopy === nodeCopy));
  const text = !isNodeEcho
    ? (labelCopy || cleaned)
    : (nodeCopy || labelCopy || cleaned);
  if (!text) return '';
  return /[.?!…]$/.test(text) ? text : `${text}...`;
};

const looksLikeRawToolArguments = (value?: string) => (
  Boolean(value && /(?:^|,)\s*[A-Za-z_][A-Za-z0-9_]*\s*=/.test(value))
);

const formatToolCallTraceLabel = (value: string | undefined, nodeName: string, fallback?: string) => (
  looksLikeRawToolArguments(value)
    ? formatTraceLabel(undefined, nodeName)
    : formatTraceLabel(value || fallback, nodeName)
);

const getDashboardHeadline = (data: DashboardData, linkedQuestion?: string | null) => {
  const title = data.title?.trim();
  if (title) return title;
  const question = linkedQuestion?.trim() || data.query?.trim();
  return question || 'Smart Insights dashboard';
};

const buildDashboardTrace = (
  progress: QueryProgress,
  includeLiveTrace: boolean,
): Repo2ProgressTraceItem[] => {
  if (!includeLiveTrace) return [];
  return progress.completedSteps
    .map((step, index) => {
      const parsed = parseRepo2TraceNode(step.node);
      const isThinking = parsed.kind === 'thinking';
      const isToolCall = parsed.kind === 'tool_call';
      const rawLabel = step.details || step.message || step.node;
      const label = isThinking
        ? formatTraceLabel(rawLabel)
        : isToolCall
          ? formatToolCallTraceLabel(rawLabel, parsed.nodeName, step.message)
          : 'Analysis complete';
      return {
        id: `${step.node}-${index}`,
        kind: parsed.kind || (isThinking ? 'thinking' as const : 'tool_result' as const),
        label,
        nodeName: parsed.nodeName,
        title: isThinking ? undefined : rawLabel,
      };
    })
    .filter((entry) => Boolean(entry.label));
};

const formatDuration = (duration?: number) => {
  if (!duration || !Number.isFinite(duration)) return '';
  const milliseconds = duration > 120 ? duration : duration * 1000;
  return milliseconds < 1000
    ? `${Math.round(milliseconds)}ms`
    : `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)}s`;
};

type Repo2ProgressTraceItem = {
  id: string;
  kind: 'thinking' | 'tool_call' | 'tool_result';
  label: string;
  nodeName?: string;
  title?: string;
  isActive?: boolean;
};

const parseRepo2TraceNode = (node: string): { kind?: Repo2ProgressTraceItem['kind']; nodeName: string } => {
  if (node.startsWith('thinking:')) return { kind: 'thinking', nodeName: node };
  const match = node.match(/^(tool_call|tool_result):(.+):\d+$/);
  if (match) {
    return {
      kind: match[1] as Repo2ProgressTraceItem['kind'],
      nodeName: match[2],
    };
  }
  return { nodeName: node };
};

const buildProgressTrace = (progress: QueryProgress, fallbackMessage?: string) => {
  const trace: Repo2ProgressTraceItem[] = progress.completedSteps
    .map((step, index) => {
      const parsed = parseRepo2TraceNode(step.node);
      const isThinking = parsed.kind === 'thinking';
      const isToolCall = parsed.kind === 'tool_call';
      const rawLabel = step.details || step.message || step.node;
      const label = isThinking
        ? formatTraceLabel(rawLabel)
        : isToolCall
          ? formatToolCallTraceLabel(rawLabel, parsed.nodeName, step.message)
          : 'Analysis complete';

      return {
        id: `${step.node}-${index}`,
        kind: parsed.kind || (isThinking ? 'thinking' as const : 'tool_result' as const),
        label,
        nodeName: parsed.nodeName,
        title: isThinking ? undefined : rawLabel,
      };
    })
    .filter((entry) => Boolean(entry.label));

  const runningNode = progress.currentNode
    ? progress.nodes.find((node) => node.name === progress.currentNode && node.status === 'running')
    : progress.nodes.find((node) => node.status === 'running');

  if (runningNode) {
    const activeLabel = formatToolCallTraceLabel(
      progress.currentDetails || progress.currentMessage || runningNode.displayName || fallbackMessage,
      runningNode.name,
      runningNode.displayName || fallbackMessage
    );
    let activeIndex = -1;
    for (let index = trace.length - 1; index >= 0; index -= 1) {
      const entry = trace[index];
      if (entry.kind === 'tool_call' && entry.nodeName === runningNode.name) {
        activeIndex = index;
        break;
      }
    }

    if (activeLabel && activeIndex >= 0) {
      trace[activeIndex] = {
        ...trace[activeIndex],
        label: activeLabel,
        isActive: true,
      };
    } else if (activeLabel) {
      trace.push({
        id: `active-${runningNode.name}`,
        kind: 'tool_call',
        label: activeLabel,
        nodeName: runningNode.name,
        title: runningNode.name,
        isActive: true,
      });
    }
  } else if (trace.length === 0) {
    const initialLabel = formatTraceLabel(fallbackMessage || progress.currentMessage || 'Reading the request');
    if (initialLabel) {
      trace.push({
        id: 'initial-thinking',
        kind: 'thinking',
        label: initialLabel,
        isActive: true,
      });
    }
  }

  return trace;
};

// Langfuse-style hierarchical trace tree, driven by
// ``progress.traceSteps``. Each node renders one row; children are
// indented under their parent via ``parentId``. The icon comes from
// ``kind`` (agent / node / tool / detector / llm) so the user sees
// instantly what kind of activity is happening.
type TraceTreeStep = {
  id: string;
  parentId: string | null;
  name: string;
  displayName: string;
  kind: 'agent' | 'node' | 'tool' | 'detector' | 'llm';
  status: 'running' | 'ok' | 'error';
  startedAt: number;
  durationMs?: number;
  summary?: string;
  milestone?: string;
};

const TraceKindIcon = ({ kind }: { kind: TraceTreeStep['kind'] }) => {
  const className = 'h-3 w-3 shrink-0';
  switch (kind) {
    case 'agent':    return <Sparkles className={`${className} text-[#6E3450]`} />;
    case 'tool':     return <Wrench   className={`${className} text-[#3F7D4F]`} />;
    case 'detector': return <Search   className={`${className} text-[#9B607A]`} />;
    case 'llm':      return <MessageSquare className={`${className} text-[#6E3450]`} />;
    case 'node':
    default:         return <Layers   className={`${className} text-[#7B6B72]`} />;
  }
};

const TraceTreeRow = ({
  step,
  depth,
  hasChildren,
  isExpanded,
  onToggle,
}: {
  step: TraceTreeStep;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const isRunning = step.status === 'running';
  const isError   = step.status === 'error';
  const detail = step.milestone || step.summary;

  return (
    <li
      className={`flex items-start gap-2 rounded-[6px] px-1 py-[3px] text-[12px] leading-snug text-[#3F3338] transition-colors ${
        isRunning ? 'bg-[#F8EFF3]/70' : ''
      }`}
      style={{ paddingLeft: depth * 14 + 4 }}
    >
      {/* Leading slot: spinner when running, chevron when collapsible,
          spacer otherwise. Spinner takes priority so the user can spot
          the actively-running row at any nesting depth without scanning
          the column. */}
      {isRunning ? (
        <span
          className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center"
          aria-label="Running"
        >
          <Loader2 className="h-3 w-3 animate-spin text-[#6E3450]" />
        </span>
      ) : hasChildren ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={isExpanded ? 'Collapse children' : 'Expand children'}
          className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#3F3338]"
        >
          {isExpanded
            ? <ChevronDown className="h-3 w-3" />
            : <ChevronRight className="h-3 w-3" />}
        </button>
      ) : (
        <span className="mt-[2px] inline-block h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      {/* Kind icon — softly pulses while running so the row "breathes"
          even at a glance. */}
      <div
        className={`mt-[2px] flex h-3.5 w-3.5 items-center justify-center ${isRunning ? 'animate-pulse' : ''}`}
      >
        <TraceKindIcon kind={step.kind} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate ${isRunning ? 'text-[#6E3450] font-medium' : isError ? 'text-[#A23E3E]' : 'text-[#3F3338]'}`}>
            {step.displayName}
          </span>
          {isRunning && (
            <span className="flex shrink-0 items-center gap-0.5 text-[#9B607A]" aria-hidden="true">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
            </span>
          )}
        </div>
        {/* Live status line — when running we surface whichever
            in-flight signal the parent emitted (currentMessage) via
            the step's milestone/summary slot if set. Otherwise show
            the persisted detail once the step finishes. */}
        {isRunning && detail && (
          <div className="mt-0.5 truncate text-[11px] italic text-[#9B607A]">
            {detail}
          </div>
        )}
        {!isRunning && detail && (
          <div className="text-[11px] text-[#7B6B72]">{detail}</div>
        )}
      </div>
    </li>
  );
};

const TraceTreeView = ({ steps }: { steps: TraceTreeStep[] }) => {
  // Manual expand/collapse overrides keyed by step id.
  // Default behaviour: running steps render expanded; once a step
  // transitions to ``ok``/``error`` it auto-collapses (children
  // disappear) so the trace stays scannable. The user can click the
  // chevron to re-open a completed step or to pre-collapse a still-
  // running one.
  const [override, setOverride] = useState<Record<string, 'expanded' | 'collapsed'>>({});

  if (!steps || steps.length === 0) {
    return (
      <li className="text-[11.5px] italic text-[#7B6B72]">
        Reading the request...
      </li>
    );
  }

  // Build child map keyed by parentId, preserving the order each step
  // first arrived (which matches start time across siblings).
  const byParent = new Map<string | null, TraceTreeStep[]>();
  for (const step of steps) {
    const list = byParent.get(step.parentId) || [];
    list.push(step);
    byParent.set(step.parentId, list);
  }

  const isExpanded = (step: TraceTreeStep): boolean => {
    const ov = override[step.id];
    if (ov) return ov === 'expanded';
    return step.status === 'running';
  };

  const toggle = (step: TraceTreeStep) => {
    setOverride((prev) => ({
      ...prev,
      [step.id]: isExpanded(step) ? 'collapsed' : 'expanded',
    }));
  };

  const rows: JSX.Element[] = [];
  const walk = (parentId: string | null, depth: number) => {
    const children = byParent.get(parentId) || [];
    for (const child of children) {
      const childHasChildren = (byParent.get(child.id) || []).length > 0;
      const expanded = isExpanded(child);
      rows.push(
        <TraceTreeRow
          key={child.id}
          step={child}
          depth={depth}
          hasChildren={childHasChildren}
          isExpanded={expanded}
          onToggle={() => toggle(child)}
        />,
      );
      if (expanded) walk(child.id, depth + 1);
    }
  };
  walk(null, 0);
  return <>{rows}</>;
};

const Repo2TraceLine = ({ entry }: { entry: Repo2ProgressTraceItem }) => {
  if (entry.kind === 'tool_call') {
    return (
      <li
        className="flex items-start gap-2 text-[12px] leading-snug text-[#3F3338]"
        title={entry.title}
      >
        <Hammer className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[#6E3450]" />
        <span className="min-w-0">
          <span>{entry.label}</span>
          {entry.isActive && (
            <span className="mt-1 flex items-center gap-1 pl-1 text-[#D5C2CB]" aria-hidden="true">
              <span className="h-1 w-1 animate-pulse rounded-full bg-current" />
              <span className="h-1 w-1 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
              <span className="h-1 w-1 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
            </span>
          )}
        </span>
      </li>
    );
  }

  if (entry.kind === 'tool_result') {
    return (
      <li
        className="flex items-start gap-1.5 text-[12px] leading-snug text-[#7B6B72]"
        title={entry.title}
      >
        <Check className="mt-[2px] h-3 w-3 shrink-0 text-[#3F7D4F]" />
        <span>{entry.label}</span>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2 text-[12px] leading-snug text-[#3F3338]">
      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#6E3450]" />
      <span>{entry.label}</span>
    </li>
  );
};

const formatMetricValue = (metric: KPIMetric) => {
  if (metric.value === null || metric.value === undefined || metric.value === '') return '--';
  if (typeof metric.value !== 'number') return String(metric.value);

  const formatted = new Intl.NumberFormat(undefined, {
    notation: Math.abs(metric.value) >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(metric.value) >= 100000 ? 1 : 2,
  }).format(metric.value);

  const unit = metric.unit?.trim();
  if (!unit) return formatted;
  if (unit === '%') return `${formatted}%`;
  if (unit === '$') return `$${formatted}`;
  return `${formatted} ${unit}`;
};

const collectPreviewMetrics = (data: DashboardData) => {
  const seen = new Set<string>();
  const metrics = [
    ...(data.metrics ?? []),
    ...((data.groups ?? []).flatMap((group) => group.metrics ?? [])),
  ];

  return metrics.filter((metric) => {
    const key = metric.id || metric.label;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 3);
};

const KpiPreviewStrip = ({ data }: { data: DashboardData }) => {
  const metrics = collectPreviewMetrics(data);
  if (metrics.length === 0) return null;

  return (
    <div
      className="mt-2 grid gap-1.5"
      style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}
    >
      {metrics.map((metric) => (
        <div
          key={metric.id || metric.label}
          className="min-w-0 rounded-[8px] border border-[#EDE6E1] bg-[#FAF7F1] px-2 py-1.5"
        >
          <div className="truncate font-display text-[9.5px] uppercase tracking-[0.12em] text-[#7B6B72]">
            {metric.label}
          </div>
          <div className="mt-1 truncate font-display text-[14px] font-semibold leading-tight text-[#5A2C58] tabular-nums">
            {formatMetricValue(metric)}
          </div>
        </div>
      ))}
    </div>
  );
};

// Backend progress rendered in the current Smart Insights loading style.
// The assistant bubble remains below as the fallback path.
const Repo2ProgressCard = ({
  message,
  progress,
  isLoading,
  isNewEntry,
}: {
  message: ChatMessage;
  progress: QueryProgress;
  isLoading: boolean;
  isNewEntry: boolean;
}) => {
  const traceSteps = (progress.traceSteps || []) as TraceTreeStep[];
  const useTraceTree = traceSteps.length > 0;
  const trace = useTraceTree ? [] : buildProgressTrace(progress, message.content);
  const stepCount = useTraceTree ? traceSteps.length : trace.length;
  const isDone = progress.isComplete || (!isLoading && !message.isStreaming);
  const [open, setOpen] = useState(true);

  // Auto-collapse the trace body once the stream finishes. Without this
  // the live trace card sits open forever above the response — every
  // sibling step row (running spinners that never get a matching
  // ``trace_step_end`` for transient sub-spans) reads as "still
  // running" to the user even though the response is already
  // delivered. We collapse after a short delay so the user briefly
  // sees the green "Insights generated" header transition before the
  // body folds away. The chevron stays clickable so users can re-open
  // the reasoning history at any time.
  const autoCollapsedRef = useRef(false);
  useEffect(() => {
    if (!isDone || autoCollapsedRef.current) return;
    autoCollapsedRef.current = true;
    const timer = setTimeout(() => setOpen(false), 800);
    return () => clearTimeout(timer);
  }, [isDone]);

  const duration = formatDuration(progress.totalDuration);

  return (
    <motion.div
      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      layout
      className="flex flex-col items-start"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] text-[#7B6B72]">
        {isDone ? (
          <CircleCheck className="h-3 w-3 text-[#3F7D4F]" />
        ) : (
          <span className="flex items-center gap-0.5">
            <span className="h-1 w-1 animate-pulse rounded-full bg-[#6E3450]" />
            <span className="h-1 w-1 animate-pulse rounded-full bg-[#6E3450] [animation-delay:150ms]" />
            <span className="h-1 w-1 animate-pulse rounded-full bg-[#6E3450] [animation-delay:300ms]" />
          </span>
        )}
        <span className="uppercase tracking-[0.18em]">
          {isDone ? `Insights generated${duration ? ` · ${duration}` : ''}` : 'AI is thinking'}
        </span>
      </div>

      <div className="min-w-0 w-full max-w-[92%] rounded-[12px] border border-[#EDE6E1] bg-white px-3 py-2.5 text-[#1F1A20] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center justify-between text-[11px] text-[#7B6B72] transition-colors hover:text-[#1F1A20]"
          aria-expanded={open}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <Hammer className="h-3 w-3 shrink-0" />
            <span className="uppercase tracking-[0.18em]">
              {isDone ? 'Reasoning trace' : 'Live trace'}
            </span>
            <span className="text-[#A8989F]">
              · {stepCount} step{stepCount === 1 ? '' : 's'}
            </span>
          </span>
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>

        {open && (
          <ol className="mt-2.5 space-y-1.5 border-t border-[#EDE6E1] pt-2.5">
            {useTraceTree ? (
              <TraceTreeView steps={traceSteps} />
            ) : trace.length === 0 ? (
              <li className="text-[11.5px] italic text-[#7B6B72]">
                Reading the request...
              </li>
            ) : trace.map((entry, index) => (
              <Repo2TraceLine key={`${entry.id}-${index}`} entry={entry} />
            ))}
          </ol>
        )}
      </div>
    </motion.div>
  );
};

// Repo-2 branding pass: screenshot-style dashboard card for Smart Insights only.
// Search "Repo-2 branding pass" to reuse or revert this styling layer later.
const DashboardReadyCard = ({
  message,
  data,
  linkedQuestion,
  isActive,
  isLatest,
  isNewEntry,
  trace,
  onOpen,
  onSuggestedQueryClick,
  showFeedbackButton,
  onFeedbackClick,
}: {
  message: ChatMessage;
  data: DashboardData;
  linkedQuestion?: string | null;
  isActive: boolean;
  isLatest: boolean;
  isNewEntry: boolean;
  trace: Repo2ProgressTraceItem[];
  onOpen: () => void;
  onSuggestedQueryClick?: (query: string) => void;
  showFeedbackButton?: boolean;
  onFeedbackClick?: () => void;
}) => {
  const [traceOpen, setTraceOpen] = useState(isLatest && trace.length > 0);
  const [followUpsOpen, setFollowUpsOpen] = useState(isLatest);
  const suggestions = message.suggestedQueries ?? [];
  const headline = getDashboardHeadline(data, linkedQuestion);

  useEffect(() => {
    if (isLatest && trace.length > 0) setTraceOpen(true);
  }, [isLatest, trace.length]);

  useEffect(() => {
    if (isLatest && suggestions.length > 0) setFollowUpsOpen(true);
  }, [isLatest, suggestions.length]);

  return (
    <motion.div
      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      layout
      className="flex flex-col items-start"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] text-[#7B6B72]">
        <CircleCheck className="h-3 w-3 text-[#3F7D4F]" />
        <span className="uppercase tracking-[0.18em]">Insights ready</span>
      </div>

      <div
        className={cn(
          "min-w-0 w-full max-w-[92%] rounded-[12px] border bg-white px-3.5 py-3 text-[#1F1A20] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]",
          isActive
            ? "border-[#6E3450]/40 ring-1 ring-[#6E3450]/15"
            : "border-[#EDE6E1]"
        )}
      >
        <div className="mb-1 text-[10.5px] uppercase tracking-[0.18em] text-[#6E3450]">
          Dashboard created for
        </div>
        <div className="text-[13px] font-semibold leading-snug text-[#1F1A20]">
          {headline}
        </div>
        <KpiPreviewStrip data={data} />

        {trace.length > 0 && (
          <div className="mt-3 border-t border-[#EDE6E1] pt-2">
            <button
              type="button"
              onClick={() => setTraceOpen((open) => !open)}
              className="flex w-full items-center justify-between text-[10.5px] text-[#7B6B72] transition-colors hover:text-[#1F1A20]"
              aria-expanded={traceOpen}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Hammer className="h-3 w-3 shrink-0" />
                <span className="uppercase tracking-[0.18em]">Reasoning trace</span>
                <span className="text-[#A8989F]">· {trace.length} step{trace.length === 1 ? '' : 's'}</span>
              </span>
              {traceOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {traceOpen && (
              <ol className="mt-2 space-y-1.5">
                {trace.map((entry, idx) => (
                  <Repo2TraceLine key={`${entry.id}-${idx}`} entry={entry} />
                ))}
              </ol>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onOpen}
          disabled={isActive}
          className={cn(
            "mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-[0.18em] transition-colors",
            isActive
              ? "cursor-default border-[#6E3450]/20 bg-[#f5e9ee] text-[#5A2C58]"
              : "border-[#6E3450] bg-[#6E3450] text-white hover:bg-[#5A2C58]"
          )}
        >
          {isActive ? (
            <>
              <CircleCheck className="h-3 w-3" />
              This dashboard is open now
            </>
          ) : (
            <>
              <ExternalLink className="h-3 w-3" />
              Open this dashboard
            </>
          )}
        </button>

      </div>

      {suggestions.length > 0 && onSuggestedQueryClick && (
        <div className="mt-3 w-full max-w-[92%]">
          <button
            type="button"
            onClick={() => setFollowUpsOpen((open) => !open)}
            className="flex w-full items-center justify-between text-[10.5px] text-[#7B6B72] transition-colors hover:text-[#1F1A20]"
            aria-expanded={followUpsOpen}
          >
            <span className="flex items-center gap-1.5">
              <ArrowUp className="h-3 w-3 rotate-45 text-[#6E3450]" />
              <span className="uppercase tracking-[0.18em]">Suggested follow-ups</span>
              <span className="text-[#A8989F]">· {suggestions.length}</span>
            </span>
            {followUpsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {followUpsOpen && (
            <div className="mt-2 grid gap-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={`${message.id}-dashboard-suggestion-${idx}`}
                  type="button"
                  onClick={() => onSuggestedQueryClick(suggestion)}
                  className={cn(
                    'group flex w-full items-start rounded-[12px] border border-[#CFA4BA] bg-[#FFFBFD] px-4 py-3 text-left shadow-none transition-all duration-200 ease-out',
                    'hover:-translate-y-0.5 hover:border-[#B77A99] hover:bg-[#FCF7FA] hover:shadow-[0_6px_16px_-12px_rgba(110,52,80,.55),0_1px_3px_rgba(31,26,32,.06)]',
                    'active:translate-y-0 active:bg-[#F8EEF4] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20 focus:ring-offset-2 focus:ring-offset-[#FAF7F1]'
                  )}
                >
                  <span className="min-w-0 flex-1 font-display text-[13.5px] leading-snug text-[#6E3450] transition-colors duration-200 group-hover:text-[#55243B]">
                    {suggestion}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showFeedbackButton && onFeedbackClick && (
        <button
          type="button"
          onClick={onFeedbackClick}
          className="mt-3 inline-flex items-center gap-1.5 rounded-[8px] border border-[#6E3450]/25 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#6E3450] transition-colors hover:bg-[#f5e9ee]/60"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Feedback
        </button>
      )}
    </motion.div>
  );
};

const hasRenderableDashboardData = (dashboardData?: DashboardData | null): dashboardData is DashboardData => (
  !!dashboardData && (
    (dashboardData.charts?.length || 0) > 0 ||
    (dashboardData.metrics?.length || 0) > 0 ||
    (dashboardData.insights?.length || 0) > 0 ||
    (dashboardData.actionItems?.length || 0) > 0 ||
    (dashboardData.tableData?.length || 0) > 0 ||
    (dashboardData.groups?.some((group) => (
      group.charts.length > 0 ||
      group.metrics.length > 0
    )) ?? false)
  )
);

const CHAT_WIDTH_SESSION_KEY = 'finance-v2-chat-width-percent';
const DASHBOARD_HIDDEN_SESSION_KEY = 'finance-v2-dashboard-hidden';
const DATA_SOURCE_SETUP_SESSION_KEY = 'finance-v2-data-source-setup-status';
const DATA_SOURCE_SETUP_ONBOARDING_SESSION_KEY = 'finance-v2-data-source-setup-onboarding';
const DEFAULT_CHAT_WIDTH_PERCENT = 32;
const MIN_CHAT_WIDTH_PERCENT = 30;
const MAX_CHAT_WIDTH_PERCENT = 62;
const MIN_CHAT_WIDTH_PX = 380;
type DataSourceSetupStatus = 'idle' | 'connecting' | 'connected' | 'error';
type DataSourceSetupMessageKind = 'just-connected' | 'welcome-back';
type DataSourceSetupOnboardingMode = 'new-user' | null;

const readSessionNumber = (key: string, fallback: number) => {
  if (typeof window === 'undefined') return fallback;
  const raw = window.sessionStorage.getItem(key);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const readSessionBoolean = (key: string, fallback = false) => {
  if (typeof window === 'undefined') return fallback;
  const raw = window.sessionStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === 'true';
};

const readDataSourceSetupStatus = (): DataSourceSetupStatus => {
  if (typeof window === 'undefined') return 'idle';
  try {
    const raw = window.sessionStorage.getItem(DATA_SOURCE_SETUP_SESSION_KEY);
    return raw === 'connecting' ? 'connecting' : 'idle';
  } catch {
    return 'idle';
  }
};

const writeDataSourceSetupStatus = (status: DataSourceSetupStatus) => {
  if (typeof window === 'undefined') return;
  try {
    if (status === 'connecting') {
      window.sessionStorage.setItem(DATA_SOURCE_SETUP_SESSION_KEY, status);
    } else {
      window.sessionStorage.removeItem(DATA_SOURCE_SETUP_SESSION_KEY);
    }
  } catch {
    // Ignore storage restrictions; the live UI state still handles the current tab.
  }
};

const readDataSourceSetupOnboardingMode = (): DataSourceSetupOnboardingMode => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(DATA_SOURCE_SETUP_ONBOARDING_SESSION_KEY) === 'new-user'
      ? 'new-user'
      : null;
  } catch {
    return null;
  }
};

const writeDataSourceSetupOnboardingMode = (mode: DataSourceSetupOnboardingMode) => {
  if (typeof window === 'undefined') return;
  try {
    if (mode === 'new-user') {
      window.sessionStorage.setItem(DATA_SOURCE_SETUP_ONBOARDING_SESSION_KEY, mode);
    } else {
      window.sessionStorage.removeItem(DATA_SOURCE_SETUP_ONBOARDING_SESSION_KEY);
    }
  } catch {
    // Ignore storage restrictions; the live UI state still handles the current tab.
  }
};

const clampPanelSize = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const formatCanvasTimestamp = (value?: Date | string | number | null): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const calculateCanvasQuestionChipLimit = (availableWidth: number, totalQuestions: number): number => {
  if (totalQuestions <= 1) return Math.max(1, totalQuestions);

  const estimatedQuestionChipWidth = 72;
  const estimatedMoreChipWidth = 70;
  const chipGap = 6;
  const safetyPadding = 8;
  const usableWidth = Math.max(0, availableWidth - safetyPadding);
  const allQuestionsWidth =
    (totalQuestions * estimatedQuestionChipWidth) +
    (Math.max(0, totalQuestions - 1) * chipGap);

  if (allQuestionsWidth <= usableWidth) {
    return totalQuestions;
  }

  const availableForQuestions = usableWidth - estimatedMoreChipWidth - chipGap;
  const questionsThatFit = Math.floor((availableForQuestions + chipGap) / (estimatedQuestionChipWidth + chipGap));
  return Math.max(1, Math.min(totalQuestions - 1, questionsThatFit));
};

const buildCanvasQuestionTabs = (
  dashboardMessages: ChatMessage[],
  activeMessageId: string,
  visibleLimit: number
) => {
  const questionTabs = dashboardMessages.map((dashboardMessage, index) => ({
    message: dashboardMessage,
    questionNumber: index + 1,
  }));
  const normalizedLimit = Math.max(1, Math.min(questionTabs.length, visibleLimit));

  if (questionTabs.length <= normalizedLimit) {
    return { visibleTabs: questionTabs, overflowTabs: [] };
  }

  const activeTab = questionTabs.find((tab) => tab.message.id === activeMessageId) ?? questionTabs[questionTabs.length - 1];

  if (normalizedLimit === 1) {
    return {
      visibleTabs: [activeTab],
      overflowTabs: questionTabs.filter((tab) => tab.message.id !== activeTab.message.id),
    };
  }

  const visibleTabs: typeof questionTabs = [];
  const visibleIds = new Set<string>();
  const firstTab = questionTabs[0];
  const reserveActiveSlot = activeTab.message.id !== firstTab.message.id;
  const fillLimit = reserveActiveSlot ? normalizedLimit - 1 : normalizedLimit;

  for (const tab of questionTabs) {
    if (visibleTabs.length >= fillLimit) break;
    if (reserveActiveSlot && tab.message.id === activeTab.message.id) continue;
    if (visibleIds.has(tab.message.id)) continue;
    visibleTabs.push(tab);
    visibleIds.add(tab.message.id);
  }

  if (reserveActiveSlot && !visibleIds.has(activeTab.message.id)) {
    visibleTabs.push(activeTab);
    visibleIds.add(activeTab.message.id);
  }

  visibleTabs.sort((a, b) => a.questionNumber - b.questionNumber);

  return {
    visibleTabs,
    overflowTabs: questionTabs.filter((tab) => !visibleIds.has(tab.message.id)),
  };
};

const formatSetupGreetingName = (value?: string | null): string => {
  const name = value?.trim();
  if (!name) return 'there';
  if (name.includes(',')) {
    const [, afterComma] = name.split(',');
    const preferred = afterComma?.trim();
    if (preferred) return preferred.split(/\s+/)[0] || preferred;
  }
  return name.split(/\s+/)[0] || name;
};

const readableErrorText = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value.map(readableErrorText).filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      readableErrorText(record.message) ||
      readableErrorText(record.detail) ||
      readableErrorText(record.error) ||
      readableErrorText(record.msg)
    );
  }
  return String(value);
};

const feedbackApiErrorMessage = (err: unknown): string => {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data;
  return (
    readableErrorText(responseData) ||
    readableErrorText((err as { message?: unknown })?.message) ||
    'Please try again later.'
  );
};

export function ChatInterface({
  userQuery,
  progress,
  isLoading,
  welcomeQuestions,
  isConnected = false,
  threadId,
  botMessageId,
  conversationMessages = [],
  dashboardDataMap = new Map(),
  currentDashboardData = null,
  conversationDataSourceName: _conversationDataSourceName,
  activeDataSourceName: _activeDataSourceName,
  onSuggestedQueryClick,
  onSubmit,
  onInterruptAction,
  onConnectDataSourceClick,
  activeDataSource,
  hasLoadedActiveDataSource = true,
  dataSourceConnectionResult,
  setupContextResetKey,
  isNewUser = null,
  userDisplayName,
  onBookmarksClick,
  onHistoryClick,
  onNewChatClick,
  isHistoryOpen = false,
  panelTitle = 'SmartInsights',
  panelSubtitle,
  onShowKpiLineage,
  onShowChartLineage,
}: ChatInterfaceProps) {
  const layoutRootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const previousStepsRef = useRef<Set<string>>(new Set());
  const messageRefs = useRef(new Map<string, HTMLDivElement | null>());
  const setupAssistantMessageRef = useRef<HTMLDivElement | null>(null);
  const userHasScrolledRef = useRef(false);
  const lastPinnedQuestionKeyRef = useRef<string | null>(null);
  const programmaticScrollUntilRef = useRef(0);
  const lastSetupScrollKeyRef = useRef<string | null>(null);
  const latestMessagesRef = useRef<ChatMessage[]>([]);
  const canvasHeaderRef = useRef<HTMLDivElement | null>(null);
  const canvasHeaderLabelRef = useRef<HTMLDivElement | null>(null);
  const canvasTimestampRef = useRef<HTMLSpanElement | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);
  const [returnToAnswerId, setReturnToAnswerId] = useState<string | null>(null);
  const [showQuestionJumpButton, setShowQuestionJumpButton] = useState(false);
  const [activeQuestionScrollSpace, setActiveQuestionScrollSpace] = useState(0);
  /** Message ID for which feedback modal was opened (use this on submit, not prop botMessageId) */
  const [feedbackTargetMessageId, setFeedbackTargetMessageId] = useState<string | null>(null);
  /** Prevent multiple submissions for the same modal open */
  const feedbackSubmittedRef = useRef(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [canvasQuestionChipLimit, setCanvasQuestionChipLimit] = useState(3);
  const [isDashboardHidden, setIsDashboardHidden] = useState(() =>
    readSessionBoolean(DASHBOARD_HIDDEN_SESSION_KEY)
  );
  const [chatWidthPercent, setChatWidthPercent] = useState(() =>
    clampPanelSize(
      readSessionNumber(CHAT_WIDTH_SESSION_KEY, DEFAULT_CHAT_WIDTH_PERCENT),
      MIN_CHAT_WIDTH_PERCENT,
      MAX_CHAT_WIDTH_PERCENT
    )
  );
  const [dataSourceSetupStatus, setDataSourceSetupStatus] = useState<DataSourceSetupStatus>(() =>
    readDataSourceSetupStatus()
  );
  const [showDataSourceSetupExchange, setShowDataSourceSetupExchange] = useState(() =>
    readDataSourceSetupStatus() === 'connecting'
  );
  const handledDataSourceConnectionResultRef = useRef<'connected' | 'error' | 'disconnected' | null>(null);
  const autoDataSourceConnectStartedRef = useRef(false);
  const autoSapReconnectErrorKeyRef = useRef<string | null>(null);
  const activeDataSourceConnectAttemptRef = useRef(false);
  const autoDataSourceConnectedTimerRef = useRef<number | null>(null);
  const [dataSourceSetupMessageKind, setDataSourceSetupMessageKind] = useState<DataSourceSetupMessageKind>('just-connected');
  const [dataSourceSetupOnboardingMode, setDataSourceSetupOnboardingMode] = useState<DataSourceSetupOnboardingMode>(() =>
    readDataSourceSetupOnboardingMode()
  );
  const [hasStartedChatAfterSetup, setHasStartedChatAfterSetup] = useState(false);
  const [showSetupTranscriptContext, setShowSetupTranscriptContext] = useState(false);
  const [hasSeenConnectedDataSource, setHasSeenConnectedDataSource] = useState(false);
  const [activeCanvasMessageId, setActiveCanvasMessageId] = useState<string | null>(null);
  const { success, error: toastError } = useGlobalToast();
  latestMessagesRef.current = messages;
  const isDataSourceConnected = !!activeDataSource;
  const backendErrorCode = extractBackendErrorCode(progress.errorMessage);
  const hasSapRefreshFailedMessage = messages.some(
    (message) => message.status === 'sap_refresh_failed' || isSapReconnectErrorCode(message.errorCode),
  );
  const hasResolvedDataSourceConnection = dataSourceConnectionResult === 'connected';
  const isSapRefreshDataSourceIssue =
    !hasResolvedDataSourceConnection &&
    (
      isSapReconnectErrorCode(backendErrorCode) ||
      hasSapRefreshFailedMessage ||
      dataSourceConnectionResult === 'error'
    );
  const dataSourceStatusState: 'connected' | 'disconnected' | 'warning' = isSapRefreshDataSourceIssue
    ? 'warning'
    : isDataSourceConnected
      ? 'connected'
      : 'disconnected';
  const setupGreetingName = formatSetupGreetingName(userDisplayName);
  const setupCardIsConnected = isDataSourceConnected;
  const isRestoredNewUserSetup = dataSourceSetupOnboardingMode === 'new-user';
  const isNewUserOnboarding = isNewUser === true || isRestoredNewUserSetup;
  const isReturningUserOnboarding = isNewUser === false && !isRestoredNewUserSetup;
  const shouldShowWelcomeBackSetup =
    isReturningUserOnboarding &&
    isDataSourceConnected &&
    dataSourceConnectionResult !== 'connected';
  const setupIntroShowsConnectedCopy = !isNewUserOnboarding && setupCardIsConnected;
  const hasQuestionInCurrentChat = hasStartedChatAfterSetup || messages.some((message) => message.role === 'user') || Boolean(userQuery?.trim());
  const hasExistingConversation = Boolean(threadId?.trim());
  const hasCompletedDataSourceConnection = isDataSourceConnected;
  const shouldHideNewChatForInitialOnboarding = isNewUserOnboarding && !hasCompletedDataSourceConnection && !hasSeenConnectedDataSource;
  const showConversationHeaderActions = isDataSourceConnected;
  const showNewChatAction =
    !shouldHideNewChatForInitialOnboarding &&
    (hasCompletedDataSourceConnection || hasSeenConnectedDataSource || isReturningUserOnboarding);
  const isChatStreamActive = isLoading || Boolean(isConnected);
  const canStartNewChat =
    Boolean(onNewChatClick) &&
    hasCompletedDataSourceConnection &&
    !isChatStreamActive &&
    (hasQuestionInCurrentChat || hasExistingConversation);
  const newChatActionTitle = !hasCompletedDataSourceConnection
    ? 'Connect a data source to start a new chat'
    : isChatStreamActive
    ? 'Wait for the current response to finish'
    : canStartNewChat
    ? 'Start a new chat'
    : 'Start a new chat after this chat has activity';
  const shouldRenderDataSourceSetup = isNewUser !== null || isRestoredNewUserSetup;
  const shouldRenderSetupIntro = isNewUserOnboarding && !shouldShowWelcomeBackSetup;
  const shouldRenderSetupUserMessage =
    isNewUserOnboarding &&
    showDataSourceSetupExchange &&
    dataSourceSetupMessageKind !== 'welcome-back';
  const canRetryDataSourceSetup =
    Boolean(onConnectDataSourceClick) && (isNewUserOnboarding || isReturningUserOnboarding);
  const shouldPreserveSetupContextForQuestion =
    shouldRenderDataSourceSetup &&
    showDataSourceSetupExchange &&
    dataSourceSetupStatus === 'connected' &&
    isDataSourceConnected;
  const shouldRestoreNewUserSetupTranscript =
    isRestoredNewUserSetup &&
    showDataSourceSetupExchange &&
    dataSourceSetupMessageKind === 'just-connected';
  const shouldRenderSetupTranscript =
    shouldRenderDataSourceSetup &&
    (
      (messages.length === 0 && !isLoading) ||
      shouldRestoreNewUserSetupTranscript ||
      (showSetupTranscriptContext && shouldPreserveSetupContextForQuestion)
    );

  // Track the current query ID to ensure loading steps are uniquely identified
  // This ref persists across renders and only changes when userQuery changes
  const currentQueryIdRef = useRef<string>('');
  const lastUserQueryRef = useRef<string>('');

  // Helper to normalize timestamp to milliseconds (handles ISO strings, epoch seconds, and epoch ms)
  const normalizeTimestampToMs = (ts: string | number | Date | undefined | null): number => {
    if (ts == null) return 0;
    if (typeof ts === 'number') {
      // If it's a small number (< year 2000 in ms), it's probably epoch seconds
      return ts < 946684800000 ? Math.round(ts * 1000) : Math.round(ts);
    }
    if (typeof ts === 'string') {
      const trimmed = ts.trim();
      // Check if it's a numeric string (epoch seconds or ms)
      if (/^\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        return num < 946684800000 ? Math.round(num * 1000) : Math.round(num);
      }
      // Otherwise, try to parse as ISO string
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) return d.getTime();
    }
    if (ts instanceof Date) {
      return ts.getTime();
    }
    return 0;
  };

  // Memoize sorted conversation messages to avoid re-sorting on every render
  // Messages are now pre-sorted by backend (user query -> bot response pairs)
  // We use humanMessageId to properly pair bot messages with their user messages
  const sortedMessages = useMemo(() => {
    // Step 1: Build a map of user message IDs/temp IDs to their messages
    const userMessageMap = new Map<string, ConversationMessage>();
    conversationMessages.forEach(m => {
      if (m.role === 'user') {
        const msgId = m.id || m.messageId || '';
        if (msgId) {
          userMessageMap.set(msgId, m);
        }
        // Also map by content for temp ID matching
        if (m.content) {
          userMessageMap.set(`content:${m.content}`, m);
        }
      }
    });

    // Step 2: Sort by timestamp, but use humanMessageId to ensure bot comes after its user message
    const decorated = conversationMessages.map((m, idx) => {
      let effectiveTime = normalizeTimestampToMs(m.timestamp);

      // For bot messages with humanMessageId, use the user message's timestamp + small offset
      // This ensures the bot message appears right after its corresponding user message
      if (m.role === 'assistant' && m.humanMessageId) {
        const humanMsgId = m.humanMessageId;
        const userMsg = userMessageMap.get(humanMsgId);
        if (userMsg) {
          // Use user message timestamp + 1ms to ensure bot appears after user
          effectiveTime = normalizeTimestampToMs(userMsg.timestamp) + 1;
        }
      }

      return { m, idx, effectiveTime };
    });

    decorated.sort((a, b) => {
      if (a.effectiveTime !== b.effectiveTime) return a.effectiveTime - b.effectiveTime;

      // Extra safety: user before assistant on exact timestamp ties
      if (a.m.role === 'user' && b.m.role === 'assistant') return -1;
      if (a.m.role === 'assistant' && b.m.role === 'user') return 1;

      return a.idx - b.idx;
    });
    return decorated.map(d => d.m);
  }, [conversationMessages]);

  const userMessagesById = useMemo(() => {
    const map = new Map<string, ChatMessage>();
    messages.forEach((message) => {
      if (message.role === 'user') {
        map.set(message.id, message);
      }
    });
    return map;
  }, [messages]);

  const latestUserMessage = useMemo(
    () => [...messages].reverse().find((message) => message.role === 'user') ?? null,
    [messages],
  );
  const latestUserMessageId = latestUserMessage?.id ?? null;
  // The backend replaces the optimistic temp ID with the persisted message ID
  // mid-stream. Content + timestamp stays stable across that replacement while
  // still distinguishing two submissions of the same question.
  const latestUserMessageKey = latestUserMessage
    ? `${latestUserMessage.content}\u0000${latestUserMessage.timestamp.getTime()}`
    : null;

  const setMessageRef = (id: string) => (node: HTMLDivElement | null) => {
    messageRefs.current.set(id, node);
  };

  const scrollToMessage = (id: string) => {
    const target = messageRefs.current.get(id);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const scrollElementNearTop = useCallback((target: HTMLElement | null, behavior: ScrollBehavior = 'smooth') => {
    const container = containerRef.current;
    if (!target || !container) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const topOffset = 24;
    const nextScrollTop = container.scrollTop + targetRect.top - containerRect.top - topOffset;

    programmaticScrollUntilRef.current = Date.now() + 1200;
    userHasScrolledRef.current = false;
    target.focus({ preventScroll: true });
    container.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior,
    });
  }, []);

  const truncateQuestion = (text: string, maxLength: number = 80) =>
    text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;

  const getLinkedQuestionForAssistant = useCallback((message: ChatMessage, messageIndex: number) => {
    const fallbackQuestion = message.role === 'assistant'
      ? [...messages.slice(0, messageIndex)].reverse().find((candidate) => candidate.role === 'user') ?? null
      : null;
    const linkedQuestion = message.humanMessageId
      ? userMessagesById.get(message.humanMessageId) ?? fallbackQuestion
      : fallbackQuestion;
    return linkedQuestion;
  }, [messages, userMessagesById]);

  const latestQuestionJumpTarget = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message.role !== 'assistant') continue;
      const linkedQuestion = getLinkedQuestionForAssistant(message, i);
      if (linkedQuestion) {
        return {
          answerId: message.id,
          questionId: linkedQuestion.id,
          preview: truncateQuestion(linkedQuestion.content, 64),
          fullText: linkedQuestion.content,
        };
      }
    }
    return null;
  }, [messages, getLinkedQuestionForAssistant]);

  const jumpToQuestion = (questionId: string, answerId: string) => {
    setHighlightedQuestionId(questionId);
    setReturnToAnswerId(answerId);
    scrollToMessage(questionId);
  };

  const jumpBackToAnswer = (answerId: string) => {
    scrollToMessage(answerId);
  };

  const clearAutoDataSourceConnectedTimer = useCallback(() => {
    if (autoDataSourceConnectedTimerRef.current === null || typeof window === 'undefined') return;
    window.clearTimeout(autoDataSourceConnectedTimerRef.current);
    autoDataSourceConnectedTimerRef.current = null;
  }, []);

  const handleSetupConnectDataSource = useCallback(async (context?: {
    retryQuery?: string | null;
    reconnectActionLabel?: string | null;
    reconnectErrorMessage?: string | null;
    reconnectErrorMessageId?: string | null;
    reconnectErrorCode?: string | null;
  }) => {
    if (!onConnectDataSourceClick || dataSourceSetupStatus === 'connecting') return;

    clearAutoDataSourceConnectedTimer();
    activeDataSourceConnectAttemptRef.current = true;
    if (isNewUserOnboarding) {
      setDataSourceSetupOnboardingMode('new-user');
      writeDataSourceSetupOnboardingMode('new-user');
    }
    setShowDataSourceSetupExchange(true);
    setDataSourceSetupStatus('connecting');
    writeDataSourceSetupStatus('connecting');

    try {
      await onConnectDataSourceClick({
        threadId,
        retryQuery: context?.retryQuery,
        reconnectActionLabel: context?.reconnectActionLabel,
        reconnectErrorMessage: context?.reconnectErrorMessage,
        reconnectErrorMessageId: context?.reconnectErrorMessageId,
        reconnectErrorCode: context?.reconnectErrorCode,
      });
    } catch {
      activeDataSourceConnectAttemptRef.current = false;
      setDataSourceSetupStatus('error');
      writeDataSourceSetupStatus('error');
    }
  }, [clearAutoDataSourceConnectedTimer, dataSourceSetupStatus, isNewUserOnboarding, onConnectDataSourceClick, threadId]);

  useEffect(() => {
    if (!progress.hasError || !progress.errorMessage) {
      autoSapReconnectErrorKeyRef.current = null;
      return;
    }

    const errorCode = extractBackendErrorCode(progress.errorMessage);
    if (!isSapReconnectErrorCode(errorCode)) return;
    if (!onConnectDataSourceClick) return;
    if (dataSourceConnectionResult === 'connected') return;
    if (dataSourceSetupStatus === 'connecting') return;

    const reconnectKey = [
      threadId ?? 'no-thread',
      userQuery?.trim() || 'no-query',
      errorCode ?? 'sap-reconnect',
      progress.errorMessage,
    ].join(':');
    if (autoSapReconnectErrorKeyRef.current === reconnectKey) return;

    autoSapReconnectErrorKeyRef.current = reconnectKey;
    void handleSetupConnectDataSource({
      retryQuery: userQuery?.trim() || null,
      reconnectErrorCode: errorCode,
    });
  }, [
    dataSourceConnectionResult,
    dataSourceSetupStatus,
    handleSetupConnectDataSource,
    onConnectDataSourceClick,
    progress.errorMessage,
    progress.hasError,
    threadId,
    userQuery,
  ]);

  const setupPromptCardsDisabled = isLoading || (!onSuggestedQueryClick && !onSubmit);

  useEffect(() => {
    setShowSetupTranscriptContext(false);
    setHasStartedChatAfterSetup(false);
  }, [setupContextResetKey]);

  const handleSetupSuggestedPromptClick = useCallback((prompt: string) => {
    if (setupPromptCardsDisabled) return;
    if (shouldPreserveSetupContextForQuestion) {
      setShowSetupTranscriptContext(true);
    }
    setHasStartedChatAfterSetup(true);
    setIsPanelCollapsed(false);
    if (onSuggestedQueryClick) {
      onSuggestedQueryClick(prompt);
      return;
    }
    // Read the user's selected effort from localStorage rather than
    // hardcoding 'normal' — suggested-prompt clicks must respect the
    // Normal / Deep toggle just like the composer textarea does.
    onSubmit?.(prompt, readPersistedAnalysisMode());
  }, [onSubmit, onSuggestedQueryClick, setupPromptCardsDisabled, shouldPreserveSetupContextForQuestion]);

  const handleComposerSubmit = useCallback((query: string, analysisMode: AnalysisMode) => {
    if (shouldPreserveSetupContextForQuestion && messages.length === 0) {
      setShowSetupTranscriptContext(true);
      setHasStartedChatAfterSetup(true);
    }
    onSubmit?.(query, analysisMode);
  }, [messages.length, onSubmit, shouldPreserveSetupContextForQuestion]);

  useEffect(() => {
    if (!hasLoadedActiveDataSource) return;
    if (dataSourceSetupStatus !== 'connecting') return;
    if (activeDataSourceConnectAttemptRef.current) return;
    if (isDataSourceConnected) return;

    setShowDataSourceSetupExchange(true);
    setDataSourceSetupStatus('error');
    writeDataSourceSetupStatus('error');
    autoDataSourceConnectStartedRef.current = false;
    clearAutoDataSourceConnectedTimer();
  }, [
    clearAutoDataSourceConnectedTimer,
    dataSourceSetupStatus,
    hasLoadedActiveDataSource,
    isDataSourceConnected,
  ]);

  useEffect(() => {
    if (!hasLoadedActiveDataSource) return;
    if (isDataSourceConnected) return;
    if (dataSourceSetupStatus !== 'connected') return;

    activeDataSourceConnectAttemptRef.current = false;
    setShowDataSourceSetupExchange(false);
    setDataSourceSetupStatus('idle');
    writeDataSourceSetupStatus('idle');
    clearAutoDataSourceConnectedTimer();
  }, [
    clearAutoDataSourceConnectedTimer,
    dataSourceSetupStatus,
    hasLoadedActiveDataSource,
    isDataSourceConnected,
  ]);

  useEffect(() => {
    const shouldHandleOnboarding = isReturningUserOnboarding || isNewUserOnboarding;

    if (!shouldHandleOnboarding) {
      autoDataSourceConnectStartedRef.current = false;
      clearAutoDataSourceConnectedTimer();
      return;
    }
    if (!hasLoadedActiveDataSource) return;
    if (messages.length > 0 || isLoading) return;

    // A refresh can still report isNewUser=true after SAP is connected.
    // In that case, treat the setup as already complete instead of asking
    // the user to connect again.
    if (isDataSourceConnected && dataSourceConnectionResult !== 'connected') {
      autoDataSourceConnectStartedRef.current = true;
      clearAutoDataSourceConnectedTimer();
      setDataSourceSetupMessageKind('welcome-back');
      setShowDataSourceSetupExchange(true);
      setDataSourceSetupStatus('connected');
      writeDataSourceSetupStatus('connected');
      return;
    }

    if (!isReturningUserOnboarding) {
      autoDataSourceConnectStartedRef.current = false;
      clearAutoDataSourceConnectedTimer();
      return;
    }

    if (autoDataSourceConnectStartedRef.current) return;
    if (showDataSourceSetupExchange || dataSourceSetupStatus === 'connecting') return;

    autoDataSourceConnectStartedRef.current = true;
    void handleSetupConnectDataSource();
  }, [
    dataSourceSetupStatus,
    handleSetupConnectDataSource,
    hasLoadedActiveDataSource,
    isDataSourceConnected,
    isLoading,
    isNewUserOnboarding,
    isReturningUserOnboarding,
    clearAutoDataSourceConnectedTimer,
    dataSourceConnectionResult,
    messages.length,
    showDataSourceSetupExchange,
  ]);

  useEffect(() => {
    if (hasCompletedDataSourceConnection) {
      setHasSeenConnectedDataSource(true);
    }
  }, [hasCompletedDataSourceConnection]);

  useEffect(() => {
    if (!highlightedQuestionId) return;
    const timer = window.setTimeout(() => {
      setHighlightedQuestionId(null);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [highlightedQuestionId]);

  useEffect(() => {
    if (!dataSourceConnectionResult) {
      handledDataSourceConnectionResultRef.current = null;
      return;
    }
    if (handledDataSourceConnectionResultRef.current === dataSourceConnectionResult) return;
    handledDataSourceConnectionResultRef.current = dataSourceConnectionResult;
    clearAutoDataSourceConnectedTimer();

    if (dataSourceConnectionResult === 'connected') {
      activeDataSourceConnectAttemptRef.current = false;
      setDataSourceSetupMessageKind('just-connected');
      setShowDataSourceSetupExchange(true);
      setDataSourceSetupStatus('connected');
      writeDataSourceSetupStatus('connected');
      writeDataSourceSetupOnboardingMode(null);
    } else if (dataSourceConnectionResult === 'error') {
      activeDataSourceConnectAttemptRef.current = false;
      setShowDataSourceSetupExchange(true);
      setDataSourceSetupStatus('error');
      writeDataSourceSetupStatus('error');
    } else if (dataSourceConnectionResult === 'disconnected') {
      activeDataSourceConnectAttemptRef.current = false;
      setDataSourceSetupMessageKind('just-connected');
      setShowDataSourceSetupExchange(false);
      setDataSourceSetupStatus('idle');
      writeDataSourceSetupStatus('idle');
    }
  }, [clearAutoDataSourceConnectedTimer, dataSourceConnectionResult]);

  useEffect(() => () => {
    clearAutoDataSourceConnectedTimer();
  }, [clearAutoDataSourceConnectedTimer]);

  useEffect(() => {
    if (!showDataSourceSetupExchange) return;

    const latestAssistantMessage = [...latestMessagesRef.current].reverse().find((message) => message.role === 'assistant');
    const scrollKey = [
      dataSourceSetupStatus,
      dataSourceSetupMessageKind,
      dataSourceConnectionResult ?? 'no-result',
      latestAssistantMessage?.id ?? 'setup-only',
    ].join(':');

    if (lastSetupScrollKeyRef.current === scrollKey) return;
    lastSetupScrollKeyRef.current = scrollKey;

    const timer = window.setTimeout(() => {
      const latestAssistantElement = latestAssistantMessage?.id
        ? messageRefs.current.get(latestAssistantMessage.id) ?? null
        : null;
      scrollElementNearTop(setupAssistantMessageRef.current ?? latestAssistantElement);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [
    dataSourceConnectionResult,
    dataSourceSetupMessageKind,
    dataSourceSetupStatus,
    scrollElementNearTop,
    showDataSourceSetupExchange,
  ]);

  const extractBotText = (raw: string): string => {
    try {
      const parsed = JSON.parse(raw);
      const structuredText = displayTextFromStructuredPayload(parsed);
      if (structuredText) return structuredText;
      // Common persisted payload shapes:
      // - { response: "..." }
      // - { data: { response: "..." } }
      // - { success: true, response: "..." }
      // - { type: "normal_text", data: { normal_text: "..." } } - Smart Insights normal_text responses
      // - { normal_text: "..." } - Direct normal_text field
	      const direct = parsed?.response;
	      const nested = parsed?.data?.response;
	      const normalText = parsed?.normal_text;
	      const nestedNormalText = parsed?.data?.normal_text;
	      const summary = parsed?.summary;
	      const reply = parsed?.reply;
	      if (typeof direct === 'string' && direct.trim()) return direct;
	      if (typeof nested === 'string' && nested.trim()) return nested;
	      if (typeof normalText === 'string' && normalText.trim()) return normalText;
	      if (typeof nestedNormalText === 'string' && nestedNormalText.trim()) return nestedNormalText;
	      if (typeof summary === 'string' && summary.trim()) return summary;
	      if (typeof reply === 'string' && reply.trim()) return reply;
	    } catch {
	      // not JSON
	    }
    return raw;
  };

  // Backend sometimes sends "Structured response with data" as error text; don't show that to users
  const isIgnoredErrorMessage = (msg: string) =>
    (msg || '').trim().toLowerCase() === 'structured response with data';

  // Convert conversation messages and progress to chat messages
  useEffect(() => {
    setMessages(prev => {
      const newMessages: ChatMessage[] = [];

      // First, add all conversation messages from history (user queries and bot responses)
      // Use memoized sorted messages
      // Track message IDs we've already added to prevent duplicates
      const addedMessageIds = new Set<string>();

      // Separate history messages from current query messages
      // Current query messages (added by FinanceBotV2Dashboard) have IDs that are:
      // - Temporary IDs starting with 'temp-user-' (before humanMessageId arrives)
      // - Or don't have a humanMessageId linkage in the history
      // History messages have UUID-style IDs from Communication Infrastructure
      const historyMessages: typeof sortedMessages = [];
      const currentQueryMessages: typeof sortedMessages = [];
      // Also track current user messages separately to ensure proper ordering
      const currentUserMessages: typeof sortedMessages = [];

      sortedMessages.forEach((msg) => {
        const msgId = msg.id || msg.messageId || '';
        // Check if this is a current query message (not from history)
        // Current query messages:
        // - Have the isLiveQuery flag (set by FinanceBotV2Dashboard for live session messages)
        // - Have temp IDs or dashboard IDs (for bot messages)
        const hasLiveQueryFlag = msg.isLiveQuery === true;
        const hasTempOrDashboardId =
          msgId.startsWith('temp-user-') ||
          msgId.startsWith('temp-bot-') ||
          msgId.startsWith('dashboard-');

        const isCurrentQueryMessage = hasLiveQueryFlag || hasTempOrDashboardId;

        if (isCurrentQueryMessage) {
          currentQueryMessages.push(msg);
          // Also track user messages separately for proper ordering
          if (msg.role === 'user') {
            currentUserMessages.push(msg);
          }
        } else {
          historyMessages.push(msg);
        }
      });

      // Process HISTORY messages first (these should be in chronological order)
      historyMessages.forEach((msg) => {
        // Skip system messages
        if (msg.role === 'system') {
          return;
        }

        // Generate unique ID
        const messageId = msg.messageId || msg.id || `msg-${msg.timestamp}-${msg.role}`;
        const botMsgId = msg.botMessageId || messageId;

        // Skip if we've already added this message (check by ID or botMessageId)
        if (addedMessageIds.has(messageId) || (msg.botMessageId && addedMessageIds.has(botMsgId))) {
          return; // Already added, skip duplicate
        }

        // Get dashboard data for this message if available (for bot messages)
        const msgDashboardData = msg.role === 'assistant'
          ? (msg.dashboardData ||
              (msg.botMessageId && dashboardDataMap.has(msg.botMessageId)
                ? dashboardDataMap.get(msg.botMessageId)
                : null))
          : null;

        const dashboardHasRenderableContent = hasRenderableDashboardData(msgDashboardData);

        // If a history bot message has no dashboard data, still render it as text.
        // Some Smart Insights replies are intentionally text-only.

        // For user messages, get content
        let displayContent = msg.content && msg.content.trim()
          ? msg.content
          : msg.message || '';
        const rawMessageText = [
          typeof msg.content === 'string' ? msg.content : '',
          typeof msg.message === 'string' ? msg.message : '',
          typeof msg.normal_text === 'string' ? msg.normal_text : '',
        ].filter(Boolean).join('\n');
        let messageErrorCode =
          normalizeBackendErrorCode(msg.errorCode ?? msg.error_code) ??
          extractBackendErrorCode(rawMessageText);

        // Skip empty user messages
        if (msg.role === 'user' && !displayContent) {
          return;
        }

        // For bot messages:
        // - If we have renderable dashboard content: use empty content (dashboard renders)
        // - Else: render the bot response text (avoid showing raw JSON if possible)
        if (msg.role === 'assistant') {
          const rawContent = displayContent;
          displayContent = dashboardHasRenderableContent ? '' : extractBotText(displayContent);
          messageErrorCode =
            messageErrorCode ??
            extractBackendErrorCode(rawContent) ??
            extractBackendErrorCode(displayContent);
          // Skip adding assistant messages that are only backend placeholder text (e.g. from error in history)
          if (!dashboardHasRenderableContent && isIgnoredErrorMessage(displayContent)) {
            return;
          }
        }

        const timestamp = msg.timestamp ? new Date(msg.timestamp) : new Date();
        // Preserve status from previous message if this update doesn't include it (avoids clearing on next state update)
        const existingMsg = prev.find(m => m.id === messageId || (msg.botMessageId && m.botMessageId === msg.botMessageId));
        const preservedStatus = isSapReconnectErrorCode(messageErrorCode)
          ? 'sap_refresh_failed'
          : (msg.status ?? existingMsg?.status);

        const chatMsg: ChatMessage = {
          id: messageId,
          role: msg.role as 'user' | 'assistant',
          content: displayContent,
          timestamp: timestamp,
          isCompleted: msg.role === 'assistant',
          isStreaming: false,
          botMessageId: msg.botMessageId,
          humanMessageId: msg.humanMessageId ?? null,
          dashboardData: dashboardHasRenderableContent ? msgDashboardData : null,
          suggestedQueries: msg.suggestedQueries,
          status: preservedStatus,
          errorCode: messageErrorCode ?? existingMsg?.errorCode ?? null,
          feedback: msg.feedback ?? existingMsg?.feedback ?? null,
        };
        newMessages.push(chatMsg);

        // Mark this message as added (by both ID and botMessageId if available)
        addedMessageIds.add(messageId);
        if (msg.botMessageId) {
          addedMessageIds.add(botMsgId);
        }
      });

      // Add current user messages (from temp IDs added by FinanceBotV2Dashboard)
      // These need to be added BEFORE loading steps
      currentUserMessages.forEach((msg) => {
        const messageId = msg.id || msg.messageId || `msg-${msg.timestamp}-user`;

        // Skip if already added
        if (addedMessageIds.has(messageId)) {
          return;
        }

        const displayContent = msg.content && msg.content.trim()
          ? msg.content
          : msg.message || '';

        // Skip empty user messages
        if (!displayContent) {
          return;
        }

        const timestamp = msg.timestamp ? new Date(msg.timestamp) : new Date();

        const chatMsg: ChatMessage = {
          id: messageId,
          role: 'user',
          content: displayContent,
          timestamp: timestamp,
          isCompleted: false,
          isStreaming: false,
        };
        newMessages.push(chatMsg);
        addedMessageIds.add(messageId);
      });

      // Current query bot messages will be handled separately below (after loading steps + dashboard)


      // Track the timestamp for the current query and its stream steps
      // This ensures all steps for a query appear after the user query
      let currentQueryTimestamp: Date | null = null;

      // Use a stable query ID that only changes when userQuery changes
      // This ensures loading steps maintain their association with the correct query across re-renders
      if (userQuery && userQuery !== lastUserQueryRef.current) {
        // New query - generate new ID
        currentQueryIdRef.current = `query-${Date.now()}`;
        lastUserQueryRef.current = userQuery;
      }
      const currentQueryId = currentQueryIdRef.current;

      // Find the maximum timestamp from conversation history to ensure current query appears after it
      const maxHistoryTimestamp = newMessages.length > 0
        ? Math.max(...newMessages.map(m => m.timestamp.getTime()))
        : Date.now();

      // Add current user query if we're processing it
      // Note: FinanceBotV2Dashboard now handles adding user messages with proper IDs
      // This is just a fallback check in case the message wasn't added yet
      const hasTraceActivity = progress.completedSteps.length > 0 || (progress.traceSteps?.length || 0) > 0;

      if (userQuery && (isLoading || hasTraceActivity || progress.isComplete)) {
        // Check if a user message with this content already exists in newMessages
        // It could have a temp ID (temp-user-*) or a real humanMessageId (UUID)
        // IMPORTANT: Find the LATEST matching message (in case same query was sent multiple times)
        const matchingMessages = newMessages.filter(m =>
          m.role === 'user' &&
          m.content === userQuery
        );
        // Get the latest one by timestamp
        const existingCurrentQuery = matchingMessages.length > 0
          ? matchingMessages.reduce((latest, current) =>
              current.timestamp.getTime() > latest.timestamp.getTime() ? current : latest
            )
          : undefined;

        if (!existingCurrentQuery) {
          // Query doesn't exist yet - add it as a new message at the bottom
          // Note: This is a fallback - FinanceBotV2Dashboard should have already added it
          // Ensure current query timestamp is AFTER all conversation history
          currentQueryTimestamp = new Date(maxHistoryTimestamp + 1000);
          const userMsg: ChatMessage = {
            id: `temp-user-${Date.now()}`, // Use temp ID - will be updated when humanMessageId arrives
            role: 'user' as const,
            content: userQuery,
            timestamp: currentQueryTimestamp,
          };
          newMessages.push(userMsg);
        } else {
          // Query already exists - ensure its timestamp is at the bottom for loading steps
          // CRITICAL FIX: Update the timestamp to be after all history if it's a current query
          // This ensures loading steps appear after the user query, not at the top
          const isCurrentQueryWithTempId = existingCurrentQuery.id?.startsWith('temp-user-');
          if (isCurrentQueryWithTempId && existingCurrentQuery.timestamp.getTime() < maxHistoryTimestamp) {
            // Update the timestamp to be after history
            existingCurrentQuery.timestamp = new Date(maxHistoryTimestamp + 1000);
          }
          currentQueryTimestamp = existingCurrentQuery.timestamp;
        }
      }

      const latestReconnectActionTime = newMessages.reduce((latest, message) => (
        message.status === 'sap_reconnect_action'
          ? Math.max(latest, message.timestamp.getTime())
          : latest
      ), 0);

      // SAP reconnect status belongs between the retried user question and
      // the live trace, not after the trace as a final assistant response.
      currentQueryMessages
        .filter((msg) => msg.role === 'assistant' && msg.status === 'sap_reconnected')
        .forEach((msg, idx) => {
          const messageId = msg.messageId || msg.id || `sap-reconnected-${idx}`;
          if (addedMessageIds.has(messageId)) return;

          const reconnectBaseTime = latestReconnectActionTime || currentQueryTimestamp?.getTime() ||
            (newMessages.length > 0
              ? Math.max(...newMessages.map(m => m.timestamp.getTime()))
              : Date.now());
          const existingMsg = prev.find(m => m.id === messageId);
          newMessages.push({
            ...existingMsg,
            id: messageId,
            role: 'assistant',
            content: msg.content || 'Data source reconnected. Retrying your question now.',
            timestamp: new Date(reconnectBaseTime + 1 + idx),
            isCompleted: true,
            isStreaming: false,
            dashboardData: null,
            status: 'sap_reconnected',
          });
          addedMessageIds.add(messageId);
        });

      // Sort all messages by timestamp again after adding current query
      newMessages.sort((a, b) => {
        return a.timestamp.getTime() - b.timestamp.getTime();
      });

      // Calculate step timestamps to ensure they appear after the user query
      // CRITICAL FIX: Always use the maximum timestamp from ALL messages (including any newly added current query)
      // This ensures loading steps for the CURRENT query always appear at the bottom, not at the top
      // even if the user query exists in history with an old timestamp
      const maxTimestamp = newMessages.length > 0
        ? Math.max(...newMessages.map(m => m.timestamp.getTime()))
        : Date.now();

      // For loading steps, ALWAYS use maxTimestamp (latest message) as base, not currentQueryTimestamp
      // This ensures current query's loading steps appear after ALL messages, including historical ones
      // Only use currentQueryTimestamp if we're sure it's the latest (i.e., it was just added)
      const isCurrentQueryNewlyAdded = currentQueryTimestamp &&
        currentQueryTimestamp.getTime() >= maxTimestamp - 100; // Allow 100ms tolerance

      const stepBaseTime = (isCurrentQueryNewlyAdded && currentQueryTimestamp)
        ? currentQueryTimestamp.getTime() + 500  // Use query timestamp if it's the latest
        : maxTimestamp + 1000;  // Otherwise, use max timestamp + offset to ensure it's after everything

      // Check if we have dashboard data for the CURRENT query
      // currentDashboardData is null when a new query starts, so this correctly shows loading
      const hasDashboardForCurrentQuery = currentDashboardData !== null && currentDashboardData !== undefined;

      // Transition from loading only when V2 has visual dashboard content.
      const hasAnyDashboardContent = hasDashboardForCurrentQuery && hasRenderableDashboardData(currentDashboardData);

      // Keep the pipeline trace as its own assistant card. When the dashboard
      // response arrives, the dashboard-ready card renders separately below it.
      const hasCurrentQueryBotResponse = currentQueryMessages.some(
        (m) => m.role === 'assistant' && (
          (m.content && String(m.content).trim()) ||
          m.normal_text ||
          hasRenderableDashboardData(m.dashboardData)
        )
      );
      const hasCompletedStepsToShow = hasTraceActivity;
      const shouldShowFinalTrace = hasCurrentQueryBotResponse && progress.isComplete && hasCompletedStepsToShow;
      // Phase 2 streaming caveat: ``hasAnyDashboardContent`` flips
      // ``true`` as soon as the FIRST kpi_card_ready frame lands, so
      // without ``isConnected`` in the gate the pipeline-status step
      // message gets evicted mid-flight and the live trace card has
      // no message to attach to. Keep the step alive while the SSE
      // stream is still open (``isConnected``); ``onclose`` flips it
      // false, and ``shouldShowFinalTrace`` then takes over to keep
      // the post-completion trace visible.
      // Empty-data short-circuit (BE ``orchestrator.empty_data_node``
      // fired). The chat surface shows ONLY the canonical "couldn't
      // fetch data" reply text — no live-trace card, no
      // pipeline-status pseudo-message, no "Insights generated"
      // chrome.
      const emptyDataShortCircuit = Boolean(progress.emptyDataShortCircuit);

      const shouldShowLoadingSteps = !emptyDataShortCircuit && (
        (isLoading || hasCompletedStepsToShow || progress.currentMessage) &&
        (!hasAnyDashboardContent || shouldShowFinalTrace || isConnected)
      );

      // Show a single pipeline/status card while the current query is running.
      const shouldShowInitialLoading = !emptyDataShortCircuit && (
        isLoading &&
        !progress.currentMessage &&
        progress.completedSteps.length === 0 &&
        !hasAnyDashboardContent
      );

      if ((shouldShowLoadingSteps || shouldShowInitialLoading) && userQuery) {
        const latestCompletedStep = progress.completedSteps[progress.completedSteps.length - 1];
        const statusContent = progress.currentMessage || latestCompletedStep?.message || latestCompletedStep?.node || 'Starting analysis';
        const statusNode = progress.currentNode || latestCompletedStep?.node || statusContent || 'query_understanding';
        const statusDetails = progress.currentMessage
          ? undefined
          : latestCompletedStep?.details || (latestCompletedStep ? `${statusContent} processed successfully` : undefined);
        const currentMaxTimestamp = newMessages.length > 0
          ? Math.max(...newMessages.map(m => m.timestamp.getTime()))
          : Date.now();
        const pipelineStatusId = `pipeline-status-${currentQueryId}`;
        const existingStatus = prev.find(m => m.id === pipelineStatusId);

        newMessages.push({
          ...existingStatus,
          id: pipelineStatusId,
          role: 'assistant',
          content: statusContent,
          timestamp: new Date(Math.max(currentMaxTimestamp + 1000, stepBaseTime + 5)),
          details: statusDetails,
          isStreaming: isLoading && !progress.isComplete && !progress.hasError,
          isCompleted: false,
          nodeName: statusNode,
          icon: getNodeIcon(statusNode, false),
        });
      }

      // Add current query bot messages (dashboard or normal_text response) at the end
      // These are messages added by FinanceBotV2Dashboard.tsx for the current query
      // - With dashboard data: show charts/metrics/insights
      // - With content only (normal_text): show bot reply as text bubble (response to user query)
      currentQueryMessages.forEach((msg) => {
        // Skip user messages - they're handled separately above
        if (msg.role === 'user' || msg.status === 'sap_reconnected') {
          return;
        }

        // Get dashboard data for this message (if any)
        const rawDashboardData = msg.dashboardData ||
          (msg.botMessageId && dashboardDataMap.has(msg.botMessageId)
            ? dashboardDataMap.get(msg.botMessageId)
            : null);

        // Only treat as dashboard if it has actual renderable content (charts/metrics/insights/intelligence or groups)
        const dashboardHasContent = hasRenderableDashboardData(rawDashboardData);
        const msgDashboardData = dashboardHasContent ? rawDashboardData : null;
        const rawInterruptOptions =
          msg.interruptOptions ||
          (rawDashboardData as any)?.meta?.interrupt_options ||
          (rawDashboardData as any)?.analysisSummary?.meta?.interrupt_options;
        const interruptOptions = Array.isArray(rawInterruptOptions)
          ? rawInterruptOptions.filter((option): option is InterruptOption => (
              Boolean(option) && typeof option === 'object' && typeof (option as InterruptOption).action === 'string'
            ))
          : undefined;

        const messageId = msg.id || msg.botMessageId || `current-dashboard-${Date.now()}`;

        // Skip if we've already added this message
        if (addedMessageIds.has(messageId)) {
          return;
        }

        // Compute timestamp so each bot response appears right after its own user query (no mixing of two queries).
        const linkedUserMsgId = msg.humanMessageId;
        const rawMessageText = [
          typeof msg.content === 'string' ? msg.content : '',
          typeof msg.message === 'string' ? msg.message : '',
          typeof msg.normal_text === 'string' ? msg.normal_text : '',
        ].filter(Boolean).join('\n');
        const messageErrorCode =
          normalizeBackendErrorCode(msg.errorCode ?? msg.error_code) ??
          extractBackendErrorCode(rawMessageText);
        const stepsEndTime = stepBaseTime + (progress.completedSteps.length * 10) + 5000;
        let messageTimestamp: Date;
        if (linkedUserMsgId) {
          const linkedUserMsg = newMessages.find(m => m.id === linkedUserMsgId);
          if (linkedUserMsg) {
            const rightAfterUser = linkedUserMsg.timestamp.getTime() + 500;
            // Only push after loading steps for the CURRENT query's response; for previous queries use rightAfterUser so order is User1, Bot1, User2, Bot2
            const isResponseToCurrentQuery = currentQueryTimestamp &&
              Math.abs(linkedUserMsg.timestamp.getTime() - currentQueryTimestamp.getTime()) < 2000;
            messageTimestamp = isResponseToCurrentQuery
              ? new Date(Math.max(rightAfterUser, stepsEndTime))
              : new Date(rightAfterUser);
          } else {
            messageTimestamp = msg.timestamp ? new Date(msg.timestamp) : new Date(stepsEndTime);
          }
        } else {
          messageTimestamp = new Date(stepsEndTime);
        }

        // Bot message with dashboard: add as dashboard block (charts, metrics, summary, intelligence)
        if (msgDashboardData) {
          // Preserve status from previous message if this update doesn't include it (avoids clearing "data from bot" on next state update)
          const existingMsg = prev.find(m => m.id === messageId || (msg.botMessageId && m.botMessageId === msg.botMessageId));
          const preservedStatus = isSapReconnectErrorCode(messageErrorCode)
            ? 'sap_refresh_failed'
            : (msg.status ?? existingMsg?.status);
          const chatMsg: ChatMessage = {
            id: messageId,
            role: 'assistant',
            content: '', // Empty content - dashboard will be shown
            timestamp: messageTimestamp,
            isCompleted: true,
            isStreaming: false,
            botMessageId: msg.botMessageId,
            humanMessageId: linkedUserMsgId ?? null,
            dashboardData: msgDashboardData,
            suggestedQueries: msg.suggestedQueries,
            interruptOptions,
            status: preservedStatus,
            errorCode: messageErrorCode ?? existingMsg?.errorCode ?? null,
            feedback: msg.feedback ?? existingMsg?.feedback ?? null,
          };
          newMessages.push(chatMsg);
          addedMessageIds.add(messageId);
          return;
        }

        // Bot message without dashboard but with content = normal_text response (plain reply to user query)
        const textContent = (msg.content && extractBotText(String(msg.content)).trim()) || msg.normal_text || '';
        if (!textContent) {
          return;
        }

        // Preserve status from previous message if this update doesn't include it (avoids clearing "data from bot" on next state update)
        const existingMsg = prev.find(m => m.id === messageId || (msg.botMessageId && m.botMessageId === msg.botMessageId));
        const finalMessageErrorCode = messageErrorCode ?? extractBackendErrorCode(textContent);
        const preservedStatus = isSapReconnectErrorCode(finalMessageErrorCode)
          ? 'sap_refresh_failed'
          : (msg.status ?? existingMsg?.status);
        const chatMsg: ChatMessage = {
          id: messageId,
          role: 'assistant',
          content: textContent,
          timestamp: messageTimestamp,
          isCompleted: true,
          isStreaming: false,
          botMessageId: msg.botMessageId,
          humanMessageId: linkedUserMsgId ?? null,
          dashboardData: null,
          suggestedQueries: msg.suggestedQueries,
          interruptOptions,
          status: preservedStatus,
          errorCode: finalMessageErrorCode ?? existingMsg?.errorCode ?? null,
          feedback: msg.feedback ?? existingMsg?.feedback ?? null,
        };
        newMessages.push(chatMsg);
        addedMessageIds.add(messageId);
      });

      // Add user-friendly error message when error occurs (skip backend-only phrases not meant for users)
      if (progress.hasError && progress.errorMessage && !isIgnoredErrorMessage(progress.errorMessage)) {
        const errorCode = extractBackendErrorCode(progress.errorMessage);
        const isSapRefreshError = isSapReconnectErrorCode(errorCode);
        if (!(isSapRefreshError && onConnectDataSourceClick)) {
          const displayMessage = isSapRefreshError
            ? stripBackendErrorCode(progress.errorMessage) || 'Your data source session needs to be reconnected.'
            : `❌ **Error**\n\n${progress.errorMessage}`;
          const persistedErrorMessageId =
            botMessageId && CHAT_FEEDBACK_UUID_RE.test(botMessageId) ? botMessageId : null;
          const errorMessageId = persistedErrorMessageId || (isSapRefreshError ? 'sap-refresh-failed' : 'error-' + Date.now());
          const existingErrorMsg = prev.find(
            (m) => m.id === errorMessageId || (persistedErrorMessageId && m.botMessageId === persistedErrorMessageId),
          );
          newMessages.push({
            id: errorMessageId,
            role: 'assistant',
            content: displayMessage,
            timestamp: new Date(),
            botMessageId: persistedErrorMessageId,
            status: isSapRefreshError ? 'sap_refresh_failed' : undefined,
            errorCode: errorCode ?? null,
            feedback: existingErrorMsg?.feedback ?? null,
          });
        }
      }

      // Final sort: use timestamp, but for assistant messages with humanMessageId use linked user timestamp + offset so each response appears right after its query (User1, Bot1, User2, Bot2)
      const userById = new Map<string, ChatMessage>();
      newMessages.forEach(m => {
        if (m.role === 'user') userById.set(m.id, m);
      });
      const sapReconnectActionTime = newMessages.reduce((latest, message) => (
        message.status === 'sap_reconnect_action'
          ? Math.max(latest, message.timestamp.getTime())
          : latest
      ), 0);
      const sapReconnectActionTimes = newMessages
        .filter((message) => message.status === 'sap_reconnect_action')
        .map((message) => message.timestamp.getTime())
        .sort((a, b) => a - b);
      const sapReconnectedTime = newMessages.reduce((latest, message) => (
        message.status === 'sap_reconnected'
          ? Math.max(latest, message.timestamp.getTime())
          : latest
      ), 0);
      const nearestSapReconnectActionTime = (messageTime: number): number => {
        if (sapReconnectActionTimes.length === 0) return 0;
        return sapReconnectActionTimes.reduce((nearest, candidate) => (
          Math.abs(candidate - messageTime) < Math.abs(nearest - messageTime)
            ? candidate
            : nearest
        ), sapReconnectActionTimes[0]);
      };
      const sapRefreshSortTime = (msg: ChatMessage): number | null => {
        if (
          msg.role === 'assistant' &&
          (msg.status === 'sap_refresh_failed' || isSapReconnectErrorCode(msg.errorCode)) &&
          sapReconnectActionTimes.length > 0
        ) {
          return nearestSapReconnectActionTime(msg.timestamp.getTime()) - 1;
        }
        return null;
      };
      const keepAfterSapReconnectConfirmation = (msg: ChatMessage, baseTime: number): number => {
        const sapErrorTime = sapRefreshSortTime(msg);
        if (sapErrorTime !== null) return sapErrorTime;
        if (msg.status === 'sap_reconnect_action') return baseTime;
        if (msg.status === 'sap_reconnected' && sapReconnectActionTimes.length > 0) {
          return nearestSapReconnectActionTime(baseTime) + 1;
        }
        if (
          sapReconnectActionTime > 0 &&
          sapReconnectedTime > 0 &&
          msg.status !== 'sap_reconnected' &&
          msg.status !== 'sap_refresh_failed' &&
          !isSapReconnectErrorCode(msg.errorCode) &&
          msg.timestamp.getTime() > sapReconnectActionTime &&
          baseTime <= sapReconnectedTime
        ) {
          return sapReconnectedTime + 1;
        }
        return baseTime;
      };
      const effectiveTime = (msg: ChatMessage): number => {
        const rawTime = msg.timestamp.getTime();
        if (msg.dashboardData) {
          return keepAfterSapReconnectConfirmation(msg, rawTime);
        }
        let baseTime = rawTime;
        if (msg.role === 'assistant' && msg.humanMessageId) {
          const userMsg = userById.get(msg.humanMessageId);
          if (userMsg) baseTime = userMsg.timestamp.getTime() + 1;
        }
        return keepAfterSapReconnectConfirmation(msg, baseTime);
      };
      const finalMessages = newMessages
        .map((m, idx) => ({ m, idx, effectiveTime: effectiveTime(m) }))
        .sort((a, b) => {
          const diff = a.effectiveTime - b.effectiveTime;
          if (diff !== 0) return diff;
          if (a.m.role === 'user' && b.m.role === 'assistant') return -1;
          if (a.m.role === 'assistant' && b.m.role === 'user') return 1;
          return a.idx - b.idx;
        })
        .map(x => x.m);

      return finalMessages;
    });
  }, [userQuery, progress, isLoading, isConnected, botMessageId, sortedMessages, dashboardDataMap, currentDashboardData, onConnectDataSourceClick]); // Include all dependencies

  // Resolve the feedback already on the message the modal is currently
  // targeting — used to pre-fill the modal so users can edit + re-submit.
  // Match by botMessageId (the persisted UUID) first, then fall back to id.
  const targetFeedback = useMemo<StoredFeedback | null>(() => {
    if (!feedbackTargetMessageId) return null;
    const target = messages.find(
      (m) => m.botMessageId === feedbackTargetMessageId || m.id === feedbackTargetMessageId,
    );
    return target?.feedback ?? null;
  }, [feedbackTargetMessageId, messages]);

  // Chat-level feedback (thumbs up/down + required text) on a single assistant
  // message. Feedback is write-once on chat-v2: the backend rejects a second
  // submit with HTTP 409 + the persisted blob, so we treat that response as a
  // sync signal and switch the modal into read-only mode without surfacing it
  // as a user error. Maps the modal's 5/1 rating onto the wire's
  // thumbs_up/thumbs_down.
  const handleFeedbackSubmit = async (rating: number, feedback: string) => {
    if (feedbackSubmittedRef.current) {
      console.warn('[ChatInterface] Feedback already submitted for this message, ignoring duplicate submit');
      return;
    }
    const messageIdForFeedback = feedbackTargetMessageId ?? botMessageId;
    const feedbackTargetMessage = messageIdForFeedback
      ? messages.find((m) => m.botMessageId === messageIdForFeedback || m.id === messageIdForFeedback)
      : null;
    const isLocalErrorFeedbackTarget =
      feedbackTargetMessage?.role === 'assistant' &&
      (
        feedbackTargetMessage.status === 'sap_refresh_failed' ||
        isSapReconnectErrorCode(feedbackTargetMessage.errorCode) ||
        feedbackTargetMessage.id === 'error' ||
        feedbackTargetMessage.id?.startsWith('error-') ||
        feedbackTargetMessage.content.trim().startsWith('❌ **Error**')
    );
    if (rating !== 5 && rating !== 1) {
      feedbackSubmittedRef.current = false;
      toastError('Rating required', 'Please choose thumbs up or thumbs down before submitting feedback.');
      return;
    }
    const wireRating: 'thumbs_up' | 'thumbs_down' = rating >= 4 ? 'thumbs_up' : 'thumbs_down';
    const trimmedText = feedback.trim();
    if (!trimmedText) {
      feedbackSubmittedRef.current = false;
      toastError('Comment required', 'Please add a comment before submitting feedback.');
      return;
    }
    if (trimmedText.length > MAX_CHAT_FEEDBACK_TEXT_LENGTH) {
      feedbackSubmittedRef.current = false;
      toastError('Comment too long', `Feedback must be ${MAX_CHAT_FEEDBACK_TEXT_LENGTH} characters or less.`);
      return;
    }
    const localFeedback: StoredFeedback = {
      rating: wireRating,
      text: trimmedText,
      submitted_at: new Date().toISOString(),
    };
    const saveLocalFeedback = () => {
      if (!messageIdForFeedback?.trim()) return false;
      setMessages((prev) =>
        prev.map((m) =>
          m.botMessageId === messageIdForFeedback || m.id === messageIdForFeedback
            ? { ...m, feedback: localFeedback }
            : m,
        ),
      );
      setShowFeedbackModal(false);
      setFeedbackTargetMessageId(null);
      success('Feedback captured!', 'Thank you for your feedback on the chat response.');
      return true;
    };

    if (
      !threadId ||
      !messageIdForFeedback ||
      !messageIdForFeedback.trim() ||
      !CHAT_FEEDBACK_UUID_RE.test(threadId) ||
      !CHAT_FEEDBACK_UUID_RE.test(messageIdForFeedback)
    ) {
      console.warn('[ChatInterface] Missing thread/message id for feedback submission', {
        threadId,
        messageIdForFeedback,
      });
      if (isLocalErrorFeedbackTarget && saveLocalFeedback()) return;
      toastError('Feedback not submitted', 'Thread or message id not available.');
      setShowFeedbackModal(false);
      return;
    }
    feedbackSubmittedRef.current = true;
    setFeedbackLoading(true);
    try {
      const result = await financeFeedbackService.submitChatFeedback({
        threadId,
        messageId: messageIdForFeedback,
        rating: wireRating,
        text: trimmedText,
      });

      if (result.status === 'submitted' || result.status === 'already_submitted') {
        // 'submitted' carries the freshly-persisted blob; 'already_submitted'
        // (HTTP 409) carries the previously-persisted blob from the server.
        // Either way, the server is now authoritative — mirror it locally so
        // the modal renders read-only on the next open.
        const authoritative = result.feedback ?? {
          rating: wireRating,
          text: trimmedText,
          submitted_at: new Date().toISOString(),
        };
        setMessages((prev) =>
          prev.map((m) =>
            m.botMessageId === messageIdForFeedback || m.id === messageIdForFeedback
              ? { ...m, feedback: authoritative }
              : m,
          ),
        );
        setShowFeedbackModal(false);
        setFeedbackTargetMessageId(null);
        if (result.status === 'submitted') {
          success('Feedback submitted!', 'Thank you for your feedback on the chat response!');
        } else {
          success(
            'Feedback already on file',
            'This response was rated previously — feedback cannot be changed.',
          );
        }
      } else if (result.status === 'skipped') {
        if (result.reason === 'missing_text') {
          feedbackSubmittedRef.current = false;
          toastError('Comment required', 'Please add a comment before submitting feedback.');
          return;
        }
        if (result.reason === 'text_too_long') {
          feedbackSubmittedRef.current = false;
          toastError('Comment too long', `Feedback must be ${MAX_CHAT_FEEDBACK_TEXT_LENGTH} characters or less.`);
          return;
        }
        if (!isLocalErrorFeedbackTarget || !saveLocalFeedback()) {
          toastError('Feedback not submitted', 'Thread or message id not available.');
          setShowFeedbackModal(false);
        }
      } else {
        feedbackSubmittedRef.current = false;
        console.error('Failed to submit feedback:', result.error);
        toastError('Feedback submission failed', feedbackApiErrorMessage(result.error));
      }
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Handle scroll events to detect user interaction
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      // Check if user is near the bottom (within 100px)
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (Date.now() > programmaticScrollUntilRef.current) {
        userHasScrolledRef.current = !isNearBottom;
      }

      if (latestQuestionJumpTarget) {
        const target = messageRefs.current.get(latestQuestionJumpTarget.questionId);
        if (target) {
          const questionBottom = target.offsetTop + target.offsetHeight;
          setShowQuestionJumpButton(questionBottom < scrollTop + 24);
        } else {
          setShowQuestionJumpButton(false);
        }
      } else {
        setShowQuestionJumpButton(false);
      }
    }
  }, [latestQuestionJumpTarget]);

  useEffect(() => {
    handleScroll();
  }, [messages, latestQuestionJumpTarget, handleScroll]);

  // Reserve one viewport of response space while the agent is working. Without
  // this runway the browser clamps scrollTop near the bottom, so a newly sent
  // question cannot actually be positioned at the top until the trace is tall.
  useEffect(() => {
    if (!isLoading || !latestUserMessageId || !containerRef.current) {
      setActiveQuestionScrollSpace(0);
      return;
    }

    setActiveQuestionScrollSpace(Math.max(0, containerRef.current.clientHeight - 64));
  }, [isLoading, latestUserMessageId]);

  // A pipeline-status message is appended in the same render as the question,
  // so checking only the final message misses normal submissions. Track the
  // latest user message directly and pin each question once, after the runway
  // above has been committed to the DOM.
  useEffect(() => {
    if (
      !isLoading ||
      !latestUserMessageId ||
      !latestUserMessageKey ||
      lastPinnedQuestionKeyRef.current === latestUserMessageKey ||
      (activeQuestionScrollSpace === 0 && (containerRef.current?.clientHeight ?? 0) > 64)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      const target = messageRefs.current.get(latestUserMessageId) ?? null;
      if (!target) return;

      lastPinnedQuestionKeyRef.current = latestUserMessageKey;
      scrollElementNearTop(target);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [activeQuestionScrollSpace, isLoading, latestUserMessageId, latestUserMessageKey, scrollElementNearTop]);

  // State for drilldown functionality
  const [selectedMetricByMessage, setSelectedMetricByMessage] = useState<Map<string, KPIMetric | null>>(new Map());
  const [dashboardSectionViewByMessage, setDashboardSectionViewByMessage] = useState<Map<string, 'accordion' | 'tabular'>>(new Map());
  const [selectedDashboardSectionByMessage, setSelectedDashboardSectionByMessage] = useState<Map<string, string>>(new Map());

  const handleMetricSelect = (messageId: string, metric: KPIMetric) => {
    setSelectedMetricByMessage(prev => {
      const newMap = new Map(prev);
      newMap.set(messageId, metric);
      return newMap;
    });
  };

  const handleMetricClose = (messageId: string) => {
    setSelectedMetricByMessage(prev => {
      const newMap = new Map(prev);
      newMap.set(messageId, null);
      return newMap;
    });
  };

  const setDashboardSectionView = (messageId: string, view: 'accordion' | 'tabular') => {
    setDashboardSectionViewByMessage((prev) => {
      const next = new Map(prev);
      next.set(messageId, view);
      return next;
    });
  };

  const setSelectedDashboardSection = (messageId: string, sectionKey: string) => {
    setSelectedDashboardSectionByMessage((prev) => {
      const next = new Map(prev);
      next.set(messageId, sectionKey);
      return next;
    });
  };

  const chatTitle = useMemo(() => {
    const firstUserMessage = messages.find((message) => message.role === 'user');
    return firstUserMessage?.content?.trim() ?? '';
  }, [messages]);

  const resolvedPanelSubtitle = useMemo(() => {
    if (panelSubtitle?.trim()) return panelSubtitle.trim();
    return chatTitle ? truncateQuestion(chatTitle, 70) : 'New conversation';
  }, [chatTitle, panelSubtitle]);

  const dashboardMessages = useMemo(
    () => messages.filter((message) => (
      message.role === 'assistant' && hasRenderableDashboardData(message.dashboardData)
    )),
    [messages]
  );
  const latestDashboardMessage = dashboardMessages[dashboardMessages.length - 1] ?? null;

  useEffect(() => {
    if (latestDashboardMessage?.id) {
      setActiveCanvasMessageId(latestDashboardMessage.id);
    }
  }, [latestDashboardMessage?.id]);

  const activeCanvasMessage = useMemo(() => (
    dashboardMessages.find((message) => message.id === activeCanvasMessageId) ?? latestDashboardMessage
  ), [activeCanvasMessageId, dashboardMessages, latestDashboardMessage]);

  const activeCanvasMessageIndex = activeCanvasMessage
    ? messages.findIndex((message) => message.id === activeCanvasMessage.id)
    : -1;
  const activeCanvasQuestion = activeCanvasMessage && activeCanvasMessageIndex >= 0
    ? getLinkedQuestionForAssistant(activeCanvasMessage, activeCanvasMessageIndex)
    : null;
  const conversationTurnCount = messages.filter((message) => message.role === 'user').length;
  const showInlineDashboards = false;

  useEffect(() => {
    const updateChipLimit = () => {
      const header = canvasHeaderRef.current;
      if (!header) return;

      const headerWidth = header.getBoundingClientRect().width;
      const labelWidth = canvasHeaderLabelRef.current?.getBoundingClientRect().width ?? 0;
      const timestampWidth = canvasTimestampRef.current?.getBoundingClientRect().width ?? 0;
      const headerGap = 16;
      const controlsGap = timestampWidth > 0 ? 8 : 0;
      const availableWidth = headerWidth - labelWidth - timestampWidth - headerGap - controlsGap;

      setCanvasQuestionChipLimit(calculateCanvasQuestionChipLimit(availableWidth, dashboardMessages.length));
    };

    updateChipLimit();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateChipLimit);
      return () => window.removeEventListener('resize', updateChipLimit);
    }

    const resizeObserver = new ResizeObserver(updateChipLimit);
    [
      canvasHeaderRef.current,
      canvasHeaderLabelRef.current,
      canvasTimestampRef.current,
    ].forEach((element) => {
      if (element) resizeObserver.observe(element);
    });
    window.addEventListener('resize', updateChipLimit);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateChipLimit);
    };
  }, [activeCanvasMessage?.id, dashboardMessages.length]);

  const persistChatWidth = useCallback((value: number) => {
    const clamped = clampPanelSize(value, MIN_CHAT_WIDTH_PERCENT, MAX_CHAT_WIDTH_PERCENT);
    setChatWidthPercent(clamped);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(CHAT_WIDTH_SESSION_KEY, String(clamped));
    }
  }, []);

  const handleResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const root = layoutRootRef.current;
    if (!root || isDashboardHidden || isPanelCollapsed) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const updateWidth = (clientX: number) => {
      const rect = root.getBoundingClientRect();
      if (rect.width <= 0) return;
      const widthFromRight = rect.right - clientX;
      const minWidthPercent = (MIN_CHAT_WIDTH_PX / rect.width) * 100;
      const nextPercent = (widthFromRight / rect.width) * 100;
      persistChatWidth(Math.max(nextPercent, minWidthPercent));
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      updateWidth(moveEvent.clientX);
    };

    const handlePointerUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    updateWidth(event.clientX);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp, { once: true });
  }, [isDashboardHidden, isPanelCollapsed, persistChatWidth]);

  const handleToggleDashboard = () => {
    setIsDashboardHidden((current) => {
      const next = !current;
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(DASHBOARD_HIDDEN_SESSION_KEY, String(next));
      }
      return next;
    });
    if (isPanelCollapsed) {
      setIsPanelCollapsed(false);
    }
  };

  const renderInsightCanvas = () => {
    const message = activeCanvasMessage;
    const data = message?.dashboardData ?? null;

    if (!message || !hasRenderableDashboardData(data)) {
      if (isNewUserOnboarding) {
        return <div className="h-full min-h-0 bg-[#FAF7F1]" />;
      }

      return (
        <div className="flex h-full min-h-0 flex-col bg-[#FAF7F1]">
          <div className="flex flex-1 items-center justify-center px-6 py-10">
            <div className="mx-auto w-full max-w-[720px] text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#f5e9ee] text-[#6E3450]">
                <ChartColumnIncreasing className="h-7 w-7" />
              </div>
              <h2 className="font-display text-[28px] font-semibold leading-[1.18] text-[#1F1A20]">
	                Welcome to SmartInsights
	              </h2>
	              <p className="mx-auto mt-3 max-w-xl text-[14px] leading-relaxed text-[#7B6B72]">
	                Your dashboard will appear here once you ask a question in the chat.
	              </p>
	              {/* Suggested prompts stay inside the chat panel; this canvas is reserved for generated dashboards. */}
	            </div>
	          </div>
	        </div>
      );
    }

    const dashboardIsLoading = isLoading && !progress.isComplete && (
      (currentDashboardData && data === currentDashboardData) ||
      (!!botMessageId && message.botMessageId === botMessageId) ||
      message.id?.startsWith('query-') ||
      message.id?.startsWith('temp-bot-') ||
      message.id?.startsWith('dashboard-')
    );
    const charts = data.charts ?? [];
    const metrics = data.metrics ?? [];
    const groups = data.groups ?? [];
    const hasCharts = charts.length > 0;
    const hasMetrics = metrics.length > 0;
    // Top of canvas: prefer the executive_summary node's CFO-grade
    // direct answer over echoing the user's question. The question
    // is still shown elsewhere (chat panel, history). Falls back to
    // the question / "Smart Insights dashboard" only when the executive
    // summary hasn't streamed in yet (e.g. the executive_summary
    // frame hasn't landed).
    const executiveSummaryText = (data.summaryText || '').trim();
    // Title holds the question (or backend-supplied title) — never the
    // executive summary. The summary is Markdown-authored prose that
    // renders below the header in its own block (see
    // ``executiveSummaryText`` rendering in the canvas body below).
    const dashboardTitle = data.title?.trim()
      || activeCanvasQuestion?.content?.trim()
      || data.query?.trim()
      || 'Smart Insights dashboard';
    const dashboardSubtitle = data.subtitle?.trim() || '';
    const canvasTimestamp = formatCanvasTimestamp(message.timestamp);
    const { visibleTabs: canvasQuestionTabs, overflowTabs: overflowCanvasQuestionTabs } = buildCanvasQuestionTabs(
      dashboardMessages,
      message.id,
      canvasQuestionChipLimit
    );

    const sortedGroups = [...groups].sort((a, b) => {
      const aOther = a.category?.toLowerCase() === 'other';
      const bOther = b.category?.toLowerCase() === 'other';
      if (aOther) return -1;
      if (bOther) return 1;
      return 0;
    });
    const filteredGroups = sortedGroups.filter((group) => {
      return group.charts.length > 0 || group.metrics.length > 0 || !!group.summary?.trim();
    });
    const inGroupMetricIds = new Set(filteredGroups.flatMap((group) => group.metrics.map((metric) => metric.id)));
    const inGroupChartKeys = new Set(filteredGroups.flatMap((group) => group.charts.map((chart) => `${chart.title}\0${chart.type}`)));
    const standaloneMetrics = metrics.filter((metric) => !inGroupMetricIds.has(metric.id));
    const orphanCharts = charts.filter((chart) => !inGroupChartKeys.has(`${chart.title}\0${chart.type}`));
    const dashboardSummaryText = data.summaryText?.trim() || '';
    const canvasSections = [
	      ...filteredGroups.map((group) => {
	        const isSummaryCard = group.category?.toLowerCase() === 'other';
	        const itemCount = group.metrics.length + group.charts.length;
	        const summaryContent = group.summary || (isSummaryCard ? dashboardSummaryText : '');

	        return {
	          key: `group-${group.category}`,
	          title: resolveDashboardSectionTitle(group),
	          helperText: resolveDashboardSectionHelperText(group, itemCount, dashboardIsLoading, Boolean(summaryContent.trim())),
	          summaryContent,
	          metrics: group.metrics,
	          charts: group.charts,
	          blockType: group.blockType,
          structuredData: group.structuredData,
        };
      }),
      ...(orphanCharts.length > 0
        ? [{
            key: 'group-additional',
            title: 'Additional',
            helperText: `${orphanCharts.length} item${orphanCharts.length === 1 ? '' : 's'} not grouped by the backend`,
            summaryContent: '',
            metrics: [],
            charts: orphanCharts,
            blockType: undefined,
            structuredData: undefined,
          }]
        : []),
    ];
    // Executive summary always renders at the top of the canvas — it
    // is the CFO-grade direct answer to the user's question
    // synthesised by the ``executive_summary`` node from KPIs / charts
    // / findings / lens / insights prose. We unconditionally surface
    // ``dashboardSummaryText`` here (the top of the canvas) instead of
    // suppressing it when sections also carry summaries; the section
    // summaries describe their own block, the top one answers the
    // question.
    const topSummaryContent = dashboardSummaryText;

    return (
      <div className="flex h-full min-h-0 flex-col bg-[#FAF7F1]">
        <div className="flex-1 overflow-y-auto px-5 py-6 lg:px-8 lg:py-7">
          <div className="mx-auto min-w-0 w-full max-w-7xl space-y-6">
            <header className="border-b border-[#EDE6E1] pb-7">
              <div ref={canvasHeaderRef} className="mb-5 flex items-center justify-between gap-4">
                <div ref={canvasHeaderLabelRef} className="flex items-center gap-2.5">
                  <Sparkle className="h-3.5 w-3.5 text-[#6E3450]" />
                  <span className="font-display text-[11px] font-semibold uppercase tracking-[0.24em] text-[#7B6B72]">
                    Insight Canvas
                  </span>
                </div>
                <div className="flex min-w-0 shrink items-center justify-end gap-2">
                  {dashboardMessages.length > 1 && (
                    <div className="flex min-w-0 items-center justify-end gap-1.5">
                      {canvasQuestionTabs.map(({ message: dashboardMessage, questionNumber }) => (
                        <button
                          key={dashboardMessage.id}
                          type="button"
                          onClick={() => setActiveCanvasMessageId(dashboardMessage.id)}
                          className={cn(
                            "whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                            dashboardMessage.id === message.id
                              ? "border-[#6E3450] bg-[#6E3450] text-white"
                              : "border-[#EDE6E1] bg-white text-[#7B6B72] hover:border-[#6E3450]/40 hover:text-[#6E3450]"
                          )}
                        >
                          Ques {questionNumber}
                        </button>
                      ))}
                      {overflowCanvasQuestionTabs.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#EDE6E1] bg-white px-2.5 py-1 text-[11px] font-medium text-[#7B6B72] transition-colors hover:border-[#6E3450]/40 hover:text-[#6E3450] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20"
                            >
                              More
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[10rem] border-[#EDE6E1] bg-white">
                            {overflowCanvasQuestionTabs.map(({ message: dashboardMessage, questionNumber }) => (
                              <DropdownMenuItem
                                key={dashboardMessage.id}
                                onSelect={() => setActiveCanvasMessageId(dashboardMessage.id)}
                                className="cursor-pointer gap-2 text-[12px] text-[#3F3338] focus:bg-[#f5e9ee] focus:text-[#6E3450]"
                              >
                                <span className="font-medium">Ques {questionNumber}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  )}
                  {canvasTimestamp && (
                    <span ref={canvasTimestampRef} className="whitespace-nowrap text-[12px] text-[#8B7A82]">
                      {canvasTimestamp}
                    </span>
                  )}
                </div>
              </div>

              <div className="relative w-full py-2 pl-6 pr-0 md:pl-7">
                <span
                  aria-hidden
                  className="absolute left-0 top-1 bottom-1 w-[4px] rounded-full bg-gradient-to-b from-[#C9A24E] via-[#6E3450]/70 to-transparent"
                />
                <div className="min-w-0 w-full">
                  <h2
                    className="font-display text-[20px] font-normal leading-[1.25] tracking-normal text-[#1F1A20] md:text-[22px] lg:text-[26px]"
                    style={{ fontVariationSettings: '"opsz" 60, "SOFT" 60' }}
                  >
                    {dashboardTitle}
                  </h2>
                  {dashboardSubtitle && dashboardSubtitle !== dashboardTitle && (
                    <p className="mt-3 w-full text-[13px] leading-relaxed text-[#7B6B72]">
                      {dashboardSubtitle}
                    </p>
                  )}
                </div>
              </div>
            </header>

	            <div className="space-y-6">
	                {/* Executive Summary — Markdown-authored direct
	                    answer to the question, produced by the
	                    backend's ``executive_summary`` node. Rendered
	                    here as readable prose with headings, lists,
	                    and emphasis intact. We pass straight through
	                    to ``MarkdownContent`` — no FE post-processing,
	                    no plain-text fallback. */}
	                {executiveSummaryText && (
	                  <section className="rounded-[12px] border border-[#EDE6E1] bg-white/70 px-5 py-4 shadow-[0_1px_0_0_rgba(31,26,32,0.03)]">
	                    <header className="mb-3 flex items-center gap-2">
	                      <span
	                        aria-hidden
	                        className="inline-block h-[3px] w-8 rounded-full bg-gradient-to-r from-[#C9A24E] to-[#6E3450]/70"
	                      />
	                      <span className="font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6E3450]">
	                        Executive Summary
	                      </span>
	                    </header>
	                    <MarkdownContent
	                      content={executiveSummaryText}
	                      className="text-[14px] leading-relaxed text-[#1F1A20]"
	                    />
	                  </section>
	                )}
	                {standaloneMetrics.length > 0 && (
	                  <Accordion
	                    type="multiple"
	                    defaultValue={['standalone-kpi-cards']}
	                    className="space-y-4"
	                  >
	                    <DashboardAccordionCard
	                      value="standalone-kpi-cards"
	                      title="KPI Cards"
	                      helperText={`${standaloneMetrics.length} KPI card${standaloneMetrics.length === 1 ? '' : 's'} in this section`}
	                    >
	                      <KPIGrid
	                        metrics={standaloneMetrics}
	                        onMetricSelect={(metric) => handleMetricSelect(message.id, metric)}
	                        selectedMetric={selectedMetricByMessage.get(message.id) || null}
	                        onMetricClose={() => handleMetricClose(message.id)}
	                        onShowLineage={onShowKpiLineage}
	                        botMessageId={message.botMessageId || undefined}
	                        showSectionChrome={false}
	                      />
	                    </DashboardAccordionCard>
	                  </Accordion>
	                )}
	                {canvasSections.length > 0 ? (
	                  <>
	                    <div className="space-y-4">
	                      {canvasSections.map((section) => {
	                        if (isNextActionsSection(section)) {
	                          return (
	                          <NextActionsPromptBlock
	                            key={section.key}
	                            section={section}
	                            onSuggestedQueryClick={onSuggestedQueryClick}
	                          />
	                          );
	                        }

	                        if (isReasoningTraceSection(section)) {
	                          return (
	                            <Accordion
	                              key={section.key}
	                              type="multiple"
	                              defaultValue={[section.key]}
	                              className="space-y-4"
	                            >
	                              <ReasoningTraceAccordionSection section={section} />
	                            </Accordion>
	                          );
	                        }

	                        const sectionContent = (
	                          <div className="min-w-0 space-y-6">
	                            {isLensSection(section) ? (
	                              <PerformanceLensSection data={lensSectionData(section)} />
	                            ) : isValueAtStakeSection(section) ? (
	                              <ValueAtStakeSection data={valueAtStakeSectionData(section)} />
	                            ) : isActionableInsightsSection(section) ? (
	                              <ActionableInsightsSection data={actionableInsightsSectionData(section)} />
	                            ) : isInsightsSection(section) ? (
	                              <InsightsLanesSection data={insightsSectionData(section)} />
	                            ) : (
	                              <DashboardSectionSummaryBlock
	                                section={section}
	                                onSuggestedQueryClick={onSuggestedQueryClick}
	                              />
	                            )}

	                            {section.metrics.length > 0 ? (
	                              <KPIGrid
	                                metrics={section.metrics}
	                                onMetricSelect={(metric) => handleMetricSelect(message.id, metric)}
	                                selectedMetric={selectedMetricByMessage.get(message.id) || null}
	                                onMetricClose={() => handleMetricClose(message.id)}
	                                onShowLineage={onShowKpiLineage}
	                                botMessageId={message.botMessageId || undefined}
	                              />
	                            ) : null}

	                            {section.charts.length > 0 && (
	                              <ChartSection
	                                charts={section.charts}
	                                botMessageId={message.botMessageId || undefined}
	                                onShowChartLineage={onShowChartLineage}
	                              />
	                            )}
	                          </div>
	                        );

	                        return isDashboardHeadlineSection(section) ? (
	                          <DashboardStaticSectionCard
	                            key={section.key}
	                            title={section.title}
	                            helperText={section.helperText}
	                          >
	                            {sectionContent}
	                          </DashboardStaticSectionCard>
	                        ) : (
	                          <Accordion
	                            key={section.key}
	                            type="multiple"
	                            defaultValue={[section.key]}
	                            className="space-y-4"
	                          >
	                            <DashboardAccordionCard
	                              value={section.key}
	                              title={section.title}
	                              helperText={section.helperText}
	                            >
	                              {sectionContent}
	                            </DashboardAccordionCard>
	                          </Accordion>
	                        );
	                      })}
	                    </div>
	                    {!hasMetrics && dashboardIsLoading && (
	                      <InlineLoadingSkeleton title="Calculating metrics..." />
	                    )}
                  </>
                ) : (
	                  <>
	                    {/* When canvasSections is empty (early streaming
	                        state — only KPI cards in flight), the
	                        ``standalone-kpi-cards`` accordion above already
	                        renders the metrics. Rendering ANOTHER KPI
	                        accordion here would duplicate the same cards
	                        until the first non-KPI block arrives, which is
	                        exactly the "KPI Cards rendered twice initially"
	                        bug. Show only the loading skeleton when nothing
	                        has streamed yet. */}
	                    {!hasMetrics && dashboardIsLoading && (
                      <SectionLoadingSkeleton title="Calculating metrics..." icon={CheckCircle} />
                    )}
                    {!hasMetrics && !hasCharts && !dashboardIsLoading && !topSummaryContent && standaloneMetrics.length === 0 && (
                      <section className={cn(repo2FinanceV2ClassNames.card, "p-8 text-center text-sm text-[#7B6B72]")}>
                        No dashboard visuals were returned for this response.
                      </section>
                    )}
                  </>
                )}
                {/* Follow-up suggestion chips at the foot of the
                    canvas — populated by the ``follow_up_questions``
                    node. The chat panel renders the same list via
                    ``message.suggestedQueries``; surfacing them here
                    too gives the user a one-click drill-down without
                    having to scroll back to the chat panel. */}
                {Array.isArray(data.suggestedQueries) && data.suggestedQueries.length > 0 && onSuggestedQueryClick && (
                  <section className="rounded-[14px] border border-[#EDE6E1] bg-white p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <Sparkle className="h-3.5 w-3.5 text-[#6E3450]" />
                      <span className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7B6B72]">
                        Suggested next questions
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {data.suggestedQueries.map((q, idx) => (
                        <button
                          key={`canvas-suggestion-${idx}`}
                          type="button"
                          onClick={() => onSuggestedQueryClick(q)}
                          className="rounded-[8px] border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2 text-left text-[12.5px] leading-snug text-[#3F3338] transition-colors hover:border-[#6E3450]/40 hover:bg-[#f5e9ee]/60 hover:text-[#6E3450]"
                          title="Click to ask this question"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </section>
                )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={layoutRootRef}
      /* Repo-2 branding pass: paper canvas, burgundy accents, and docked chat shell. */
      className={cn("relative flex h-full min-h-0 w-full overflow-hidden rounded-none sm:rounded-xl", repo2FinanceV2ClassNames.pageShell)}
    >
      {!isDashboardHidden && (
        <section className="min-w-0 flex-1 overflow-hidden">
          {renderInsightCanvas()}
        </section>
      )}

      {!isDashboardHidden && !isPanelCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat panel"
          title="Drag to resize chat"
          onPointerDown={handleResizeStart}
          className="group relative z-20 flex w-2 shrink-0 cursor-col-resize items-center justify-center bg-transparent"
        >
          <span className="h-12 w-px rounded-full bg-[#EDE6E1] transition-colors group-hover:bg-[#6E3450]/45" />
        </div>
      )}

      <AnimatePresence initial={false}>
        {!isPanelCollapsed && (
          <motion.aside
            key="finance-v2-chat-panel"
            initial={{ opacity: 0, x: 48 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 48 }}
            transition={{ duration: 0.24, ease: 'easeInOut' }}
            style={isDashboardHidden ? undefined : { width: `${chatWidthPercent}%` }}
            className={cn(
              "z-10 flex h-full min-w-[min(100%,380px)] flex-col bg-white shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]",
              isDashboardHidden
                ? "w-full flex-1 border-l-0"
                : "max-w-[760px] shrink-0 border-l border-[#EDE6E1]"
            )}
          >
            <header className="shrink-0 border-b border-[#EDE6E1] bg-white px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f5e9ee] text-[#6E3450]">
                    <ChartColumnIncreasing className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-bold leading-tight text-[#1F1A20]">
                      {panelTitle}
                    </div>
                    <div className="truncate text-[10.5px] leading-tight text-[#7B6B72]">
                      {resolvedPanelSubtitle}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                  {showNewChatAction && (
                    <PanelActionButton
                      title={newChatActionTitle}
                      onClick={canStartNewChat ? onNewChatClick : undefined}
                      disabled={!canStartNewChat}
                      icon={<Plus className="h-4 w-4" />}
                    />
                  )}
	                  {showConversationHeaderActions && onHistoryClick && (
	                    <PanelActionButton
	                      title={isHistoryOpen ? 'Chat history open' : 'Open chat history'}
	                      onClick={onHistoryClick}
	                      active={isHistoryOpen}
	                      icon={<History className="h-4 w-4" />}
	                    />
	                  )}
                  {showConversationHeaderActions && onBookmarksClick && (
                    <PanelActionButton
                      title="Saved conversations"
                      onClick={() => {
                        void onBookmarksClick();
                      }}
                      icon={<Star className="h-4 w-4" />}
                    />
                  )}
	                  <PanelActionButton
	                    title={isDashboardHidden ? 'Show dashboard' : 'Hide dashboard'}
	                    onClick={handleToggleDashboard}
	                    active={isDashboardHidden}
	                    icon={isDashboardHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
	                  />
	                  <PanelActionButton
	                    title="Collapse chat"
	                    onClick={() => {
	                      if (isDashboardHidden) {
	                        setIsDashboardHidden(false);
	                        if (typeof window !== 'undefined') {
	                          window.sessionStorage.setItem(DASHBOARD_HIDDEN_SESSION_KEY, 'false');
	                        }
	                      }
	                      setIsPanelCollapsed(true);
	                    }}
	                    icon={<Minus className="h-4 w-4" />}
	                  />
                </div>
              </div>
            </header>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto scroll-thin bg-[#FAF7F1] px-4 py-4"
      >
        <div className="w-full space-y-4">
          {shouldRenderSetupTranscript && (
            <div className="space-y-3 pt-2">
              {shouldRenderSetupIntro && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.24, ease: 'easeOut' }}
                  className="flex justify-start scroll-mt-24"
                >
                  <div className="w-full max-w-[27rem] rounded-[12px] rounded-bl-[4px] border border-[#D9B7C7] bg-white px-4 py-4 text-left shadow-[0_1px_0_rgba(31,26,32,.04),0_10px_24px_-18px_rgba(110,52,80,.35)] sm:max-w-[75%]">
                    <p className="font-display text-[14px] font-medium leading-6 text-[#3F3338]">
                      Hi {setupGreetingName} 👋
                    </p>
                    <p className="mt-4 font-display text-[14px] font-medium leading-6 text-[#3F3338]">
                      We&apos;re getting you set up on SmartInsights.
                    </p>
                    <p className="mt-4 font-display text-[14px] font-medium leading-6 text-[#3F3338]">
                      {setupIntroShowsConnectedCopy
                        ? 'Your data source is connected. Ask a business question whenever you are ready.'
                        : 'To get started, click the button below to connect your data sources so I can personalize insights and help answer your business questions.'}
                    </p>
                    {!setupIntroShowsConnectedCopy && !showDataSourceSetupExchange && (
                      <button
                        type="button"
                        onClick={() => {
                          void handleSetupConnectDataSource();
                        }}
                        className="mt-4 inline-flex min-h-9 items-center justify-center rounded-[10px] border border-[#D9B7C7] bg-[#FCF7FA] px-3.5 py-1.5 text-[12.5px] font-semibold text-[#6E3450] transition-colors hover:border-[#CFA4BA] hover:bg-[#F5E9EE] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20"
                      >
                        Connect to data source
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {showDataSourceSetupExchange && (
                <>
                  {shouldRenderSetupUserMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                      className="flex justify-end scroll-mt-24"
                    >
                      <div className="max-w-[88%] rounded-[12px] rounded-br-[4px] bg-[#6E3450] px-3.5 py-2 text-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] sm:max-w-[75%]">
                        <p className="whitespace-pre-wrap text-[13px] font-normal leading-snug">
                          Connect to data source
                        </p>
                      </div>
                    </motion.div>
                  )}

                  <motion.div
                    ref={setupAssistantMessageRef}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, ease: 'easeOut' }}
                    className="flex justify-start scroll-mt-24 focus:outline-none"
                    tabIndex={-1}
                  >
	                    {setupCardIsConnected ? (
	                      <div className="w-full max-w-[27rem] space-y-2 sm:max-w-[75%]">
	                        <div className="rounded-[12px] rounded-bl-[4px] border border-[#D9B7C7] bg-white px-4 py-4 text-left text-[#3F3338] shadow-[0_1px_0_rgba(31,26,32,.04),0_10px_24px_-18px_rgba(110,52,80,.35)]">
	                            {dataSourceSetupMessageKind === 'welcome-back' ? (
	                              <>
	                                <p className="font-display text-[14px] font-medium leading-6 text-[#3F3338]">
	                                  Welcome back to SmartInsights. 👋
	                                </p>
                                  <p className="mt-4 font-display text-[14px] font-medium leading-6">
                                    Your latest business performance view is ready.
                                  </p>
	                                <p className="mt-1 font-display text-[14px] font-medium leading-6">
	                                  Start by asking a question, and I&apos;ll generate the right insights &amp; dashboard.
	                                </p>
	                              </>
                            ) : (
                              <>
                                <p className="font-display text-[14px] font-medium leading-6">
                                  Great, everything&apos;s ready to go.
                                  <span className="ml-1 inline-block align-[-1px]" role="img" aria-label="Clap">
                                    👏
                                  </span>
                                </p>
                                <p className="mt-4 font-display text-[14px] font-medium leading-6">
                                  I&apos;m ready to help you explore your data, understand key trends, and uncover actionable insights.
                                </p>
                                <p className="mt-4 font-display text-[14px] font-medium leading-6">
                                  {welcomeQuestions.length > 0
                                    ? 'Start with one of the suggested questions below, or ask anything you\'d like to explore.'
                                    : 'Ask anything you\'d like to explore.'}
                                  <span className="ml-1 inline-block align-[-1px]" role="img" aria-label="Pointing down">
                                    👇
                                  </span>
                                  
                                </p>
                              </>
                            )}
	                        </div>
                        <div className="grid gap-2">
                          {welcomeQuestions.map((prompt) => (
                            <button
                              key={`setup-suggested-${prompt}`}
                              type="button"
                              onClick={() => handleSetupSuggestedPromptClick(prompt)}
                              disabled={setupPromptCardsDisabled}
                              className={cn(
                                'group flex w-full items-start rounded-[12px] border border-[#CFA4BA] bg-[#FFFBFD] px-4 py-3 text-left shadow-none transition-all duration-200 ease-out',
                                'hover:-translate-y-0.5 hover:border-[#B77A99] hover:bg-[#FCF7FA] hover:shadow-[0_6px_16px_-12px_rgba(110,52,80,.55),0_1px_3px_rgba(31,26,32,.06)]',
                                'active:translate-y-0 active:bg-[#F8EEF4] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20 focus:ring-offset-2 focus:ring-offset-[#FAF7F1]',
                                'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:border-[#CFA4BA] disabled:hover:bg-[#FFFBFD] disabled:hover:shadow-none'
                              )}
                            >
                              <span className="min-w-0 flex-1 font-display text-[13.5px] leading-snug text-[#6E3450] transition-colors duration-200 group-hover:text-[#55243B]">
                                {prompt}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex max-w-[85%] items-start gap-2.5 rounded-[12px] rounded-bl-[4px] border border-[#EDE6E1] bg-white px-3 py-2.5 text-[#3F3338] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] sm:max-w-[75%]">
                        <div
                          className={cn(
                            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
                            dataSourceSetupStatus === 'error'
                              ? 'border-[#B23A48]/25 bg-[#FBEDEF] text-[#B23A48]'
                              : 'border-[#6E3450]/30 bg-[#f5e9ee] text-[#6E3450]'
                          )}
                        >
                          {dataSourceSetupStatus === 'error' ? (
                            <span className="text-[13px] font-bold leading-none">!</span>
                          ) : (
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#6E3450] border-t-transparent" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold leading-5 text-[#1F1A20]">
                            {dataSourceSetupStatus === 'error'
                              ? "Couldn't connect to data source"
                              : 'Connecting your data source'}
                          </p>
                          <p className="mt-0.5 text-[12.5px] leading-5 text-[#7B6B72]">
                            {dataSourceSetupStatus === 'error'
                              ? 'The connection did not become active.'
                              : 'Opening the secure connection flow. This may take a moment.'}
                          </p>
                          {/* {dataSourceSetupStatus === 'error' && canRetryDataSourceSetup && (
                            <button
                              type="button"
                              onClick={() => {
                                void handleSetupConnectDataSource();
                              }}
                              className="mt-2 inline-flex min-h-8 items-center justify-center rounded-[9px] border border-[#D9B7C7] bg-[#FCF7FA] px-3 py-1 text-[12px] font-semibold text-[#6E3450] transition-colors hover:border-[#CFA4BA] hover:bg-[#F5E9EE] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20"
                            >
                              Try to reconnect
                            </button>
                          )} */}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </>
              )}
            </div>
          )}

          <AnimatePresence mode="popLayout">
            {messages.map((message, index) => {
              // Only animate entry for new messages (not updates to current-step)
              const isNewEntry = message.id === 'current-step'
                ? false
                : !previousStepsRef.current.has(message.id);

              if (message.id !== 'current-step' && !message.isStreaming) {
                previousStepsRef.current.add(message.id);
              }

              // Check if this is a completed assistant message and if next message is also completed
              // to draw connecting line
              const isCompletedAssistant = message.role === 'assistant' && message.isCompleted;
              const nextMessage = messages[index + 1];
              const hasNextCompletedAssistant = nextMessage?.role === 'assistant' &&
                (nextMessage?.isCompleted || nextMessage?.isStreaming);
              const showConnectingLine = isCompletedAssistant && hasNextCompletedAssistant;

              const hasDashboard = message.role === 'assistant' && hasRenderableDashboardData(message.dashboardData);

              // Normal_text response: no visual dashboard — use dedicated component.
              // Exclude streaming messages and loading steps (step-*, current-step-* or nodeName) so those use the step UI (green checkmark / spinner).
              const isLoadingStep = message.role === 'assistant' && (
                message.id?.startsWith('step-') ||
                message.id?.startsWith('current-step-') ||
                message.nodeName != null
              );
              const isSapRefreshFailedMessage =
                message.role === 'assistant' &&
                (message.status === 'sap_refresh_failed' || isSapReconnectErrorCode(message.errorCode));
              const isNormalTextResponse = message.role === 'assistant' && !!message.content?.trim() && !hasDashboard && !message.isStreaming && !isLoadingStep && !isSapRefreshFailedMessage;
              const linkedQuestion = getLinkedQuestionForAssistant(message, index);
              const isStatusUpdate = isLoadingStep && message.role === 'assistant';
              const isSyntheticErrorMessage =
                message.role === 'assistant' &&
                (
                  isSapRefreshFailedMessage ||
                  message.id === 'error' ||
                  message.id?.startsWith('error-') ||
                  message.content.trim().startsWith('❌ **Error**')
                );
              const persistedFeedbackMessageId = message.role === 'assistant'
                ? getPersistedFeedbackMessageId(message)
                : null;
              const canShowFeedbackForMessage =
                message.role === 'assistant' &&
                !message.isStreaming &&
                !isStatusUpdate &&
                (Boolean(persistedFeedbackMessageId) || (!isSapRefreshFailedMessage && isSyntheticErrorMessage));
              const hasFollowUpAfterSapError = isSapRefreshFailedMessage && messages.slice(index + 1).some((candidate) => (
                candidate.status === 'sap_reconnect_action' ||
                candidate.status === 'sap_reconnected' ||
                candidate.role === 'user' ||
                (candidate.role === 'assistant' && !candidate.isStreaming && !candidate.nodeName)
              ));
              const canShowSapReconnectButton =
                Boolean(onConnectDataSourceClick) &&
                dataSourceConnectionResult !== 'connected' &&
                !hasFollowUpAfterSapError;

              return (
                <div
                  key={message.id}
                  ref={setMessageRef(message.id)}
                  className="w-full focus:outline-none"
                  tabIndex={message.role === 'assistant' ? -1 : undefined}
                >
                  {/* User message - always show as chat bubble on the right */}
                  {message.role === 'user' && (
                    <motion.div
                      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      layout
                      className="relative flex justify-end scroll-mt-24"
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        /* Repo-2 branding pass: screenshot-style burgundy user prompt bubble. */
                        className={cn(
                          "max-w-[88%] rounded-[12px] rounded-br-[4px] bg-[#6E3450] px-3.5 py-2 text-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] transition-all duration-300 sm:max-w-[75%]",
                          highlightedQuestionId === message.id && "ring-2 ring-[#6E3450]/25 ring-offset-2 ring-offset-[#FAF7F1]"
                        )}
                      >
                        <motion.p
                          key={`${message.id}-${message.content}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="whitespace-pre-wrap text-[13px] font-normal leading-snug"
                        >
                          {message.content}
                        </motion.p>
                        {returnToAnswerId && highlightedQuestionId === message.id && (
                          <button
                            type="button"
                            onClick={() => jumpBackToAnswer(returnToAnswerId)}
                            className="mt-2 inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-white/20"
                          >
                            <CornerRightUp className="h-3.5 w-3.5" />
                            Back to answer
                          </button>
                        )}
                      </motion.div>
                    </motion.div>
                  )}

	                  {isSapRefreshFailedMessage && (
	                    <motion.div
	                      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
	                      animate={{ opacity: 1, y: 0 }}
	                      exit={{ opacity: 0 }}
	                      transition={{ duration: 0.3, ease: 'easeOut' }}
	                      layout
	                      className="flex flex-col items-start scroll-mt-24"
	                    >
	                      <div className="flex w-full max-w-[27rem] items-start gap-2.5 rounded-[12px] rounded-bl-[4px] border border-amber-200 bg-white px-4 py-3.5 text-left text-[#3F3338] shadow-[0_1px_0_rgba(31,26,32,.04),0_10px_24px_-18px_rgba(180,83,9,.25)] sm:max-w-[75%]">
	                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700">
	                          <DatabaseBackup className="h-4 w-4" />
	                        </div>
	                        <div className="min-w-0 flex-1">
	                          {/* <p className="font-display text-[13.5px] font-semibold leading-5 text-[#1F1A20]">
	                            Data Source Connection Failed
	                          </p> */}
	                          <MarkdownContent
	                            content={message.content}
	                            className="mt-1 text-[13px] leading-relaxed text-[#3F3338]"
	                          />
	                          {canShowSapReconnectButton && (
	                            <button
	                              type="button"
	                              onClick={() => {
                                  const persistedMessageId = getPersistedFeedbackMessageId(message);
	                                void handleSetupConnectDataSource({
	                                  retryQuery: linkedQuestion?.content,
	                                  reconnectActionLabel: 'Reconnect data source',
	                                  reconnectErrorMessage: message.content,
	                                  reconnectErrorMessageId: persistedMessageId,
	                                  reconnectErrorCode: message.errorCode,
	                                });
	                              }}
	                              disabled={dataSourceSetupStatus === 'connecting'}
	                              className="mt-3 inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[9px] border border-amber-300 bg-amber-50 px-3 py-1 text-[12px] font-semibold text-amber-800 transition-colors hover:border-amber-400 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
	                            >
	                              <DatabaseBackup className="h-3.5 w-3.5" />
	                              Reconnect data source
	                            </button>
	                          )}
	                        </div>
	                      </div>
	                      {canShowFeedbackForMessage && (
	                        <button
	                          type="button"
	                          onClick={() => {
	                            setFeedbackTargetMessageId(persistedFeedbackMessageId || message.botMessageId || message.id || null);
	                            feedbackSubmittedRef.current = false;
	                            setShowFeedbackModal(true);
	                          }}
	                          className="mt-2 inline-flex items-center gap-1.5 rounded-[8px] border border-[#6E3450]/25 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#6E3450] transition-colors hover:bg-[#f5e9ee]/60"
	                          title="Provide feedback"
	                        >
	                          <MessageSquare className="h-3.5 w-3.5" />
	                          Feedback
	                        </button>
	                      )}
	                    </motion.div>
	                  )}

                  {/* Repo-2 branding pass: real backend loading/progress rendered as a repo-2 trace card.
                    *
                    * Phase 2 streaming sets a partial dashboard
                    * response as soon as the FIRST kpi_card_ready
                    * frame arrives, which previously hid this trace
                    * card mid-flight. Keep it visible until the SSE
                    * stream physically closes (``isConnected``
                    * mirrors the ``onopen`` / ``onclose`` callbacks
                    * — flips false only after ``complete`` /
                    * ``onclose`` lands, never on intermediate
                    * frames). That preserves the live trace through
                    * the entire ``final`` → ``saved`` → ``complete``
                    * tail of the stream. The partial dashboard
                    * still renders below; the two coexist for the
                    * duration of streaming. */}
                  {message.role === 'assistant'
                    && (!hasDashboard || isConnected || isLoading)
                    && !isNormalTextResponse
                    && isStatusUpdate
                    && (
                    <Repo2ProgressCard
                      message={message}
                      progress={progress}
                      isLoading={isLoading}
                      isNewEntry={isNewEntry}
                    />
                  )}

                  {/* Text-only assistant bubble fallback for non-dashboard, non-status replies. */}
                  {message.role === 'assistant' && !hasDashboard && !isNormalTextResponse && !isStatusUpdate && !isSapRefreshFailedMessage && (
                    <motion.div
                      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
	                      animate={{ opacity: 1, y: 0 }}
	                      exit={{ opacity: 0 }}
	                      transition={{ duration: 0.3, ease: 'easeOut' }}
	                      layout
	                      className="flex flex-col items-start"
	                    >
	                      <div className="flex gap-2.5 relative justify-start">
	                        <div className="relative flex-shrink-0">
	                          {/* Connecting line to next node - green when connecting completed steps */}
	                          {showConnectingLine && (
	                            <motion.div
	                              initial={{ scaleY: 0 }}
	                              animate={{ scaleY: 1 }}
	                              transition={{ duration: 0.3, delay: 0.2 }}
	                              className={cn(
	                                "absolute left-1/2 top-8 w-[2px] h-[calc(100%+12px)] -translate-x-1/2 origin-top rounded-full",
	                                message.isCompleted && nextMessage?.isCompleted
	                                  ? "bg-gradient-to-b from-[#3F7D4F] via-[#6f9f79] to-[#3F7D4F]"
	                                  : "bg-gradient-to-b from-[#6E3450] via-[#7c4660] to-[#6E3450]"
	                              )}
	                              style={{ zIndex: 0 }}
	                            />
	                          )}
	                          <motion.div
	                            initial={{ scale: 0 }}
	                            animate={{ scale: 1 }}
	                            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
	                            className={cn(
	                              "relative z-10 w-8 h-8 rounded-full flex items-center justify-center border shadow-sm",
	                              message.isCompleted
	                                ? "border-[#3F7D4F] bg-[#3F7D4F] text-white shadow-md shadow-[#3F7D4F]/15 ring-2 ring-[#3F7D4F]/20"
	                                : "border-[#6E3450]/30 bg-[#f5e9ee] text-[#6E3450]"
	                            )}
	                          >
	                            {message.isStreaming ? (
	                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#6E3450] border-t-transparent" />
	                            ) : message.isCompleted ? (
	                              <CheckCircle className="h-5 w-5 text-white flex-shrink-0" aria-label="Step completed" />
	                            ) : message.icon ? (
	                              message.icon
	                            ) : (
	                              <CheckCircle className="h-4 w-4" />
	                            )}
	                          </motion.div>
	                        </div>

	                        <motion.div
	                          initial={{ opacity: 0 }}
	                          animate={{ opacity: 1 }}
	                          transition={{ duration: 0.2 }}
	                          className={cn(
	                            isStatusUpdate
	                              ? 'max-w-[85%] rounded-[12px] border border-[#EDE6E1] bg-white/85 px-3 py-2 text-[#3F3338] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]'
	                              : 'max-w-[85%] rounded-[12px] rounded-bl-[4px] border border-[#EDE6E1] bg-white px-3 py-2.5 text-[#3F3338] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] sm:max-w-[75%]',
	                            message.id === 'error' && 'text-[#B23A48]'
	                          )}
	                        >
                        {message.content && (
                          <motion.div
                            key={`${message.id}-${message.content}`}
                            initial={{ opacity: 0 }}
                            animate={{
                              opacity: message.isStreaming ? [0.7, 1, 0.7] : 1
                            }}
                            transition={{
                              duration: message.isStreaming ? 1.5 : 0.3,
                              repeat: message.isStreaming ? Infinity : 0,
                              ease: 'easeInOut'
                            }}
                            className={cn(
                              "text-[13px] leading-relaxed",
                              message.isStreaming && "animate-pulse-subtle",
                              message.isCompleted && "text-[#3F7D4F]",
                              isStatusUpdate ? "font-medium" : "font-normal"
                            )}
                          >
                            {message.isStreaming ? (
                              <p className="inline-flex items-center gap-2 whitespace-pre-wrap">
                                {isStatusUpdate && (
                                  <span className="rounded-full bg-[#f5e9ee] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6E3450]">
                                    Status
                                  </span>
                                )}
                                <span>{message.content}</span>
                                {!isStatusUpdate && <TypingDots />}
                              </p>
                            ) : (
                              <MarkdownContent content={message.content} className="text-[13px] leading-relaxed text-[#3F3338]" />
                            )}
                          </motion.div>
                        )}
                        {message.details && (
                          <motion.p
                            key={`${message.id}-details-${message.details}`}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-1.5 text-[11px] text-[#7B6B72]"
                          >
                            {message.details}
                          </motion.p>
                        )}
                        {/* Suggested follow-up queries for normal_text responses */}
                        {message.suggestedQueries && message.suggestedQueries.length > 0 && onSuggestedQueryClick && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.suggestedQueries.map((suggestion, idx) => (
                              <button
                                key={`${message.id}-suggestion-${idx}`}
                                type="button"
                                onClick={() => onSuggestedQueryClick(suggestion)}
                                className="rounded-[8px] border border-[#EDE5D8] bg-white px-2.5 py-1.5 text-left text-[11px] font-medium text-[#6E3450] shadow-sm transition-colors hover:border-[#6E3450]/40 hover:bg-[#f5e9ee]/60"
                                title="Click to ask this question with the same data source"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                        {message.interruptOptions && message.interruptOptions.length > 0 && onInterruptAction && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.interruptOptions
                              .filter((option) => option.action !== 'clarify' || Boolean(option.value))
                              .map((option, idx) => {
                                const label = (option.label || option.action.replace(/_/g, ' '))
                                  .replace(/^\d+\.\s*/, '');
                                return (
                                  <button
                                    key={`${message.id}-interrupt-option-${idx}`}
                                    type="button"
                                    disabled={isLoading}
                                    onClick={() => onInterruptAction(option.action, label, option.value)}
                                    className="rounded-[8px] border border-[#D9A5BE] bg-white px-2.5 py-1.5 text-left text-[11px] font-semibold text-[#6E3450] shadow-sm transition-colors hover:border-[#6E3450]/50 hover:bg-[#f5e9ee]/70 disabled:cursor-not-allowed disabled:opacity-50"
                                    title={option.description || label}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                          </div>
                        )}
	                        </motion.div>

	                        {/* Show feedback for persisted assistant responses and local error cards. */}
	                        {canShowFeedbackForMessage && !isSyntheticErrorMessage && (
	                          <motion.button
	                            initial={{ opacity: 0, scale: 0.8 }}
	                            animate={{ opacity: 1, scale: 1 }}
	                            transition={{ delay: 0.5, duration: 0.3 }}
	                            onClick={() => {
	                              setFeedbackTargetMessageId(message.botMessageId || message.id || null);
	                              feedbackSubmittedRef.current = false;
	                              setShowFeedbackModal(true);
	                            }}
	                            className="ml-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#6E3450]/30 bg-[#f5e9ee] shadow-sm transition-all duration-200 hover:scale-105 hover:bg-[#f5e9ee]/75"
	                            title="Provide feedback"
	                          >
	                            <MessageSquare className="h-3.5 w-3.5 text-[#6E3450]" />
	                          </motion.button>
	                        )}
	                      </div>
	                      {canShowFeedbackForMessage && isSyntheticErrorMessage && (
	                        <button
	                          type="button"
	                          onClick={() => {
	                            setFeedbackTargetMessageId(message.botMessageId || message.id || null);
	                            feedbackSubmittedRef.current = false;
	                            setShowFeedbackModal(true);
	                          }}
	                          className="ml-10 mt-2 inline-flex items-center gap-1.5 rounded-[8px] border border-[#6E3450]/25 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#6E3450] transition-colors hover:bg-[#f5e9ee]/60"
	                          title="Provide feedback"
	                        >
	                          <MessageSquare className="h-3.5 w-3.5" />
	                          Feedback
	                        </button>
	                      )}
	                    </motion.div>
	                  )}

                  {/* Normal_text: all UI in NormalTextResponse (text, suggestion cards, feedback) */}
	                  {isNormalTextResponse && (
	                    <NormalTextResponse
	                      content={message.content}
                      suggestedQueries={message.suggestedQueries}
                      onSuggestedQueryClick={onSuggestedQueryClick}
                      showFeedbackButton={!!message.botMessageId && !message.isStreaming}
                      onFeedbackClick={() => {
                        setFeedbackTargetMessageId(message.botMessageId || message.id || null);
                        feedbackSubmittedRef.current = false;
                        setShowFeedbackModal(true);
                      }}
                      isNewEntry={isNewEntry}
	                    />
	                  )}

                  {/* "Insights ready" badge — split the gate by
                    * message identity:
                    *
                    *   - Live streaming message (the one whose
                    *     dashboardData matches ``currentDashboardData``
                    *     OR shares the active ``botMessageId`` OR has
                    *     a temp/query/dashboard id while a stream is
                    *     in flight): wait for the stream to close
                    *     AND the run to complete, so we don't show a
                    *     half-rendered partial card.
                    *
                    *   - Historical messages (everything else,
                    *     including previous turns in this session and
                    *     messages loaded from thread history): render
                    *     unconditionally. Without this split the
                    *     previous turn's card vanishes the instant
                    *     the user clicks a follow-up chip and the
                    *     next stream starts (``isLoading=true``). */}
                  {hasDashboard && (() => {
                    // No active stream → there is no "live" message.
                    // Render every dashboard card unconditionally.
                    // Covers: thread history view, previous turns in
                    // this session before/after a follow-up click,
                    // and the post-stream state of the current run.
                    const streamActive = isLoading || isConnected;
                    if (!streamActive) return true;

                    // Active stream → identify the one message that
                    // belongs to the in-flight run. Only that one
                    // gets gated on completion; every other dashboard
                    // card stays visible.
                    //
                    // Heuristics (one is enough):
                    //  - id matches the saved botMessageId (final/saved frame landed)
                    //  - dashboardData is the same object reference
                    //    as ``currentDashboardData`` (set every time
                    //    the streaming response transforms)
                    //
                    // We deliberately DON'T treat ``id starts with
                    // 'dashboard-'`` as "live" — every previous turn
                    // in this session shares that prefix, which is
                    // exactly what hid those cards before.
                    const matchesCurrentBotMessageId =
                      !!botMessageId && message.botMessageId === botMessageId;
                    const isMessageDisplayingCurrentData =
                      !!currentDashboardData && message.dashboardData === currentDashboardData;
                    const isLiveMessage =
                      matchesCurrentBotMessageId
                      || isMessageDisplayingCurrentData;

                    // Live message: wait for the stream to close AND
                    // the run to complete before flipping the
                    // "Insights ready" badge — otherwise the badge
                    // appears mid-render. Non-live messages render now.
                    return isLiveMessage
                      ? (!isConnected && progress.isComplete && !isLoading)
                      : true;
                  })() && (
	                    <DashboardReadyCard
	                      message={message}
	                      data={message.dashboardData!}
	                      linkedQuestion={linkedQuestion?.content}
	                      isActive={activeCanvasMessage?.id === message.id}
	                      isLatest={latestDashboardMessage?.id === message.id}
	                      isNewEntry={isNewEntry}
		                      trace={[]}
                      onOpen={() => {
                        setActiveCanvasMessageId(message.id);
                        if (isDashboardHidden) {
                          setIsDashboardHidden(false);
                          if (typeof window !== 'undefined') {
                            window.sessionStorage.setItem(DASHBOARD_HIDDEN_SESSION_KEY, 'false');
                          }
                        }
                      }}
                      onSuggestedQueryClick={onSuggestedQueryClick}
                      showFeedbackButton={!!message.botMessageId && !message.isStreaming}
                      onFeedbackClick={() => {
                        setFeedbackTargetMessageId(message.botMessageId || message.id || null);
                        feedbackSubmittedRef.current = false;
                        setShowFeedbackModal(true);
                      }}
                    />
                  )}

	                  {/* Display full dashboard inline after bot completion message - FULL WIDTH */}
	                  {/* Show dashboard when we have dashboard data OR when we're loading the current query and have partial data (summary_ready / metrics_ready / intelligence_ready) */}
	                  {hasDashboard && showInlineDashboards && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                      className="mt-4 mb-8 flex justify-start gap-3 sm:gap-4"
                    >
                      <div className="flex-shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary/20 bg-primary/10">
                          <Brain className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div className="min-w-0 max-w-[95%] flex-1 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:max-w-[90%]">
                        {/* Check if this message is for the current loading query */}
                        {(() => {
                        // ONLY show loading on the CURRENT query's message - never on past responses.
                        // Identify current message by: it displays currentDashboardData (same reference),
                        // or has temp/query/dashboard ID, or matches botMessageId when it has arrived.
                        const isMessageDisplayingCurrentData = currentDashboardData && message.dashboardData === currentDashboardData;
                        const hasCurrentQueryId = message.id?.startsWith('query-') || message.id?.startsWith('temp-bot-') || message.id?.startsWith('dashboard-');
                        const matchesCurrentBotMessageId = !!botMessageId && message.botMessageId === botMessageId;
                        const isCurrentQueryResponse = isMessageDisplayingCurrentData || (hasCurrentQueryId && isLoading) || matchesCurrentBotMessageId;
                        const dashboardIsLoading = isLoading && !progress.isComplete && isCurrentQueryResponse;
                        const hasCharts = message.dashboardData?.charts && message.dashboardData.charts.length > 0;
                        const hasMetrics = message.dashboardData?.metrics && message.dashboardData.metrics.length > 0;
                        const groups = message.dashboardData?.groups;
                        const hasGroups = groups && groups.length > 0;

                        const sortedGroups = hasGroups
                          ? [...groups!].sort((a, b) => {
                              const aOther = a.category?.toLowerCase() === 'other';
                              const bOther = b.category?.toLowerCase() === 'other';
                              if (aOther) return -1;
                              if (bOther) return 1;
                              return 0;
                            })
                          : [];

                        const filteredGroups = sortedGroups.filter((group) => {
                          return group.charts.length > 0 || group.metrics.length > 0 || !!group.summary?.trim();
                        });

                        // Charts/metrics in top-level data but not in any group (e.g. when backend sends groups that don't include all items)
                        const inGroupMetricIds = new Set(filteredGroups.flatMap((g) => g.metrics.map((m) => m.id)));
                        const inGroupChartKeys = new Set(filteredGroups.flatMap((g) => g.charts.map((c) => `${c.title}\0${c.type}`)));
                        const standaloneMetrics = (message.dashboardData?.metrics ?? []).filter((m) => !inGroupMetricIds.has(m.id));
                        const orphanCharts = (message.dashboardData?.charts ?? []).filter((c) => !inGroupChartKeys.has(`${c.title}\0${c.type}`));
                        const dashboardSummaryText = message.dashboardData?.summaryText?.trim() || '';
                        const dashboardSections = [
	                          ...filteredGroups.map((group) => {
	                            const isSummaryCard = group.category?.toLowerCase() === 'other';
	                            const itemCount = group.metrics.length + group.charts.length;
	                            const summaryContent = group.summary || (isSummaryCard ? dashboardSummaryText : '');
	                            return {
	                              key: `group-${group.category}`,
	                              title: resolveDashboardSectionTitle(group),
	                              helperText: resolveDashboardSectionHelperText(group, itemCount, dashboardIsLoading, Boolean(summaryContent.trim())),
	                              summaryContent,
	                              group,
	                              orphan: false,
	                            };
                          }),
                          ...(orphanCharts.length > 0
                            ? [{
                                key: 'group-additional',
                                title: 'Additional',
                                helperText: `${orphanCharts.length} item${orphanCharts.length === 1 ? '' : 's'} not grouped by the backend`,
                                summaryContent: '',
                                group: {
                                  category: 'additional',
                                  summary: '',
                                  metrics: [],
                                  charts: orphanCharts,
                                  blockType: undefined,
                                  structuredData: undefined,
                                },
                                orphan: true,
                              }]
                            : []),
                        ];
                        const showDashboardSectionViewToggle = false;
                        const dashboardSectionView = showDashboardSectionViewToggle
                          ? (dashboardSectionViewByMessage.get(message.id) ?? 'accordion')
                          : 'accordion';
                        const activeDashboardSectionKey = selectedDashboardSectionByMessage.get(message.id) ?? dashboardSections[0]?.key;
                        const activeDashboardSection = dashboardSections.find((section) => section.key === activeDashboardSectionKey) ?? dashboardSections[0];
                        const topSummaryContent = dashboardSections.some((section) => section.summaryContent?.trim())
                          ? ''
                          : dashboardSummaryText;

                        return (
                          <>
                          <Tabs defaultValue="dashboard" className="w-full">
                            <div className="flex justify-center mb-6">
                              <TabsList className="inline-flex h-9 items-center justify-center rounded-lg bg-muted/50 p-1 text-muted-foreground border border-border/50">
                                <TabsTrigger
                                  value="dashboard"
                                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
                                >
                                  <BarChart3 className="h-4 w-4" />
                                  Dashboard
                                </TabsTrigger>
                              </TabsList>
                            </div>

	                            <TabsContent value="dashboard" className="space-y-6">
	                              <DashboardSummaryBlock content={topSummaryContent} />
	                              {standaloneMetrics.length > 0 && (
	                                <Accordion
	                                  type="multiple"
	                                  defaultValue={['standalone-kpi-cards']}
	                                  className="space-y-4"
	                                >
	                                  <DashboardAccordionCard
	                                    value="standalone-kpi-cards"
	                                    title="KPI Cards"
	                                    helperText={`${standaloneMetrics.length} KPI card${standaloneMetrics.length === 1 ? '' : 's'} in this section`}
	                                  >
	                                    <KPIGrid
	                                      metrics={standaloneMetrics}
	                                      onMetricSelect={(metric) => handleMetricSelect(message.id, metric)}
	                                      selectedMetric={selectedMetricByMessage.get(message.id) || null}
	                                      onMetricClose={() => handleMetricClose(message.id)}
	                                      onShowLineage={onShowKpiLineage}
	                                      botMessageId={message.botMessageId || undefined}
	                                      showSectionChrome={false}
	                                    />
	                                  </DashboardAccordionCard>
	                                </Accordion>
	                              )}
	                              {hasGroups && filteredGroups.length > 0 ? (
	                                <div className="space-y-4">
                                  {showDashboardSectionViewToggle && (
                                    <div className="flex items-center justify-center">
                                      <div className="inline-flex rounded-lg border border-border/60 bg-muted/40 p-1">
                                        <button
                                          type="button"
                                          onClick={() => setDashboardSectionView(message.id, 'accordion')}
                                          className={cn(
                                            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                            dashboardSectionView === 'accordion'
                                              ? "bg-primary text-primary-foreground shadow-sm"
                                              : "text-muted-foreground hover:text-foreground"
                                          )}
                                        >
                                          Accordion
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setDashboardSectionView(message.id, 'tabular');
                                            if (dashboardSections[0]) setSelectedDashboardSection(message.id, dashboardSections[0].key);
                                          }}
                                          className={cn(
                                            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                            dashboardSectionView === 'tabular'
                                              ? "bg-primary text-primary-foreground shadow-sm"
                                              : "text-muted-foreground hover:text-foreground"
                                          )}
                                        >
                                          Tabular
                                        </button>
                                      </div>
                                    </div>
                                  )}

	                                  {dashboardSectionView === 'accordion' ? (
	                                    <div className="space-y-4">
	                                      {dashboardSections.map((section) => {
	                                        if (isNextActionsSection(section)) {
	                                          return (
	                                            <NextActionsPromptBlock
	                                              key={section.key}
	                                              section={section}
	                                              onSuggestedQueryClick={onSuggestedQueryClick}
	                                            />
	                                          );
	                                        }

	                                        if (isReasoningTraceSection(section)) {
	                                          return (
	                                            <Accordion
	                                              key={section.key}
	                                              type="multiple"
	                                              defaultValue={[section.key]}
	                                              className="space-y-4"
	                                            >
	                                              <ReasoningTraceAccordionSection section={section} />
	                                            </Accordion>
	                                          );
	                                        }

	                                        const sectionContent = (
	                                          <div className="min-w-0 space-y-6">
	                                            {(section.group.category?.toLowerCase() !== 'other' || section.group.charts.length > 0 || section.group.metrics.length > 0 || !!section.summaryContent?.trim()) && (
	                                              <>
	                                                {isLensSection(section) ? (
	                                                  <PerformanceLensSection data={lensSectionData(section)} />
	                                                ) : isValueAtStakeSection(section) ? (
	                                                  <ValueAtStakeSection data={valueAtStakeSectionData(section)} />
	                                                ) : isInsightsSection(section) ? (
	                                                  <InsightsLanesSection data={insightsSectionData(section)} />
	                                                ) : (
	                                                  <DashboardSectionSummaryBlock
	                                                    section={section}
	                                                    onSuggestedQueryClick={onSuggestedQueryClick}
	                                                  />
	                                                )}

	                                                {section.group.metrics.length > 0 ? (
	                                                  <div>
	                                                    <KPIGrid
	                                                      metrics={section.group.metrics}
	                                                      onMetricSelect={(m) => handleMetricSelect(message.id!, m)}
	                                                      selectedMetric={selectedMetricByMessage.get(message.id!) || null}
	                                                      onMetricClose={() => handleMetricClose(message.id!)}
	                                                      onShowLineage={onShowKpiLineage}
	                                                      botMessageId={message.botMessageId || undefined}
	                                                    />
	                                                  </div>
	                                                ) : null}

	                                                {section.group.charts.length > 0 && (
	                                                  <div>
	                                                    <ChartSection
	                                                      charts={section.group.charts}
	                                                      botMessageId={message.botMessageId || undefined}
	                                                      onShowChartLineage={onShowChartLineage}
	                                                    />
	                                                  </div>
	                                                )}
	                                              </>
	                                            )}
	                                          </div>
	                                        );

	                                        return isDashboardHeadlineSection(section) ? (
	                                          <DashboardStaticSectionCard
	                                            key={section.key}
	                                            title={section.title}
	                                            helperText={section.helperText}
	                                          >
	                                            {sectionContent}
	                                          </DashboardStaticSectionCard>
	                                        ) : (
	                                          <Accordion
	                                            key={section.key}
	                                            type="multiple"
	                                            defaultValue={[section.key]}
	                                            className="space-y-4"
	                                          >
	                                            <DashboardAccordionCard
	                                              value={section.key}
	                                              title={section.title}
	                                              helperText={section.helperText}
	                                            >
	                                              {sectionContent}
	                                            </DashboardAccordionCard>
	                                          </Accordion>
	                                        );
	                                      })}
	                                    </div>
	                                  ) : (
                                    <div className="space-y-4">
                                      <div className="overflow-x-auto">
                                        <div className="flex min-w-max gap-2 rounded-xl border border-border/60 bg-card/70 p-2">
                                          {dashboardSections.map((section) => (
                                            <button
                                              key={section.key}
                                              type="button"
                                              onClick={() => setSelectedDashboardSection(message.id, section.key)}
                                              className={cn(
                                                "rounded-lg px-3 py-2 text-left transition-colors",
                                                activeDashboardSection?.key === section.key
                                                  ? "bg-primary text-primary-foreground shadow-sm"
                                                  : "bg-muted/30 text-foreground hover:bg-muted/60"
                                              )}
                                            >
                                              <div className="whitespace-nowrap text-sm font-semibold">
                                                {section.title}
                                              </div>
                                            </button>
                                          ))}
                                        </div>
                                      </div>

                                      {activeDashboardSection && (
                                        isNextActionsSection(activeDashboardSection) ? (
                                          <NextActionsPromptBlock
                                            section={activeDashboardSection}
                                            onSuggestedQueryClick={onSuggestedQueryClick}
                                          />
                                        ) : (
                                        <Card className="overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
                                          <CardHeader className="border-b border-[#EDE6E1] pb-4">
                                            <CardTitle className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-[#7B6B72]">
                                              {activeDashboardSection.title}
                                            </CardTitle>
                                            {activeDashboardSection.helperText && (
                                              <CardDescription className="font-display text-[14px] leading-relaxed text-[#8B7A82]">
                                                {activeDashboardSection.helperText}
                                              </CardDescription>
                                            )}
                                          </CardHeader>
                                          <CardContent className="space-y-6 pt-5">
                                            {isLensSection(activeDashboardSection) ? (
                                              <PerformanceLensSection data={lensSectionData(activeDashboardSection)} />
                                            ) : isValueAtStakeSection(activeDashboardSection) ? (
                                              <ValueAtStakeSection data={valueAtStakeSectionData(activeDashboardSection)} />
                                            ) : isActionableInsightsSection(activeDashboardSection) ? (
                                              <ActionableInsightsSection data={actionableInsightsSectionData(activeDashboardSection)} />
                                            ) : isInsightsSection(activeDashboardSection) ? (
                                              <InsightsLanesSection data={insightsSectionData(activeDashboardSection)} />
                                            ) : (
                                              <DashboardSectionSummaryBlock
                                                section={activeDashboardSection}
                                                onSuggestedQueryClick={onSuggestedQueryClick}
                                              />
                                            )}

	                                            {activeDashboardSection.group.metrics.length > 0 ? (
	                                              <KPIGrid
	                                                metrics={activeDashboardSection.group.metrics}
	                                                onMetricSelect={(m) => handleMetricSelect(message.id!, m)}
                                                selectedMetric={selectedMetricByMessage.get(message.id!) || null}
                                                onMetricClose={() => handleMetricClose(message.id!)}
	                                                onShowLineage={onShowKpiLineage}
	                                                botMessageId={message.botMessageId || undefined}
	                                              />
	                                            ) : null}

                                            {activeDashboardSection.group.charts.length > 0 && (
                                              <ChartSection
                                                charts={activeDashboardSection.group.charts}
                                                botMessageId={message.botMessageId || undefined}
                                                onShowChartLineage={onShowChartLineage}
                                              />
                                            )}

                                          </CardContent>
                                        </Card>
                                        )
                                      )}
                                    </div>
                                  )}
	                                  {!hasMetrics && dashboardIsLoading && (
	                                    <InlineLoadingSkeleton title="Calculating metrics..." />
	                                  )}
	                                </div>
	                              ) : (
	                                <>
	                                  {hasMetrics ? (
	                                    <Accordion
	                                      type="multiple"
	                                      defaultValue={['standalone-kpi-cards']}
	                                      className="space-y-4"
	                                    >
	                                      <DashboardAccordionCard
	                                        value="standalone-kpi-cards"
	                                        title="KPI Cards"
	                                        helperText={`${message.dashboardData!.metrics.length} KPI card${message.dashboardData!.metrics.length === 1 ? '' : 's'} in this section`}
	                                      >
	                                        <KPIGrid
	                                          metrics={message.dashboardData!.metrics}
	                                          onMetricSelect={(metric) => handleMetricSelect(message.id, metric)}
	                                          selectedMetric={selectedMetricByMessage.get(message.id) || null}
	                                          onMetricClose={() => handleMetricClose(message.id)}
	                                          onShowLineage={onShowKpiLineage}
	                                          botMessageId={message.botMessageId || undefined}
	                                          showSectionChrome={false}
	                                        />
	                                      </DashboardAccordionCard>
	                                    </Accordion>
	                                  ) : dashboardIsLoading && (
                                    <SectionLoadingSkeleton title="Calculating metrics..." icon={CheckCircle} />
                                  )}
                                  {hasCharts && (
                                    <section>
                                      <ChartSection
                                        charts={message.dashboardData!.charts}
                                        botMessageId={message.botMessageId || undefined}
                                        onShowChartLineage={onShowChartLineage}
                                      />
                                    </section>
                                  )}
                                  {!hasMetrics && !hasCharts && !dashboardIsLoading && !topSummaryContent && standaloneMetrics.length === 0 && (
                                    <section className="rounded-xl border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground shadow-sm">
                                      No dashboard visuals were returned for this response.
                                    </section>
                                  )}
                                </>
                              )}
                            </TabsContent>
                          </Tabs>
                          {/* Suggested follow-up queries as buttons (dashboard and normal_text) */}
                          {message.suggestedQueries && message.suggestedQueries.length > 0 && onSuggestedQueryClick && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {message.suggestedQueries.map((suggestion, idx) => (
                                <button
                                  key={`${message.id}-suggestion-${idx}`}
                                  type="button"
                                  onClick={() => onSuggestedQueryClick(suggestion)}
                                  className="px-3 py-1.5 text-xs rounded-full border border-primary/40 text-primary font-medium bg-primary/5 hover:bg-primary/10 hover:text-primary-700 transition-colors shadow-sm hover:shadow-md"
                                  title="Click to ask this question with the same data source"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                          {message.interruptOptions && message.interruptOptions.length > 0 && onInterruptAction && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {message.interruptOptions
                                .filter((option) => option.action !== 'clarify' || Boolean(option.value))
                                .map((option, idx) => {
                                  const label = (option.label || option.action.replace(/_/g, ' '))
                                    .replace(/^\d+\.\s*/, '');
                                  return (
                                    <button
                                      key={`${message.id}-interrupt-option-${idx}`}
                                      type="button"
                                      disabled={isLoading}
                                      onClick={() => onInterruptAction(option.action, label, option.value)}
                                      className="px-3 py-1.5 text-xs rounded-full border border-primary/40 text-primary font-medium bg-primary/5 hover:bg-primary/10 hover:text-primary-700 transition-colors shadow-sm hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                                      title={option.description || label}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                          </>
                        );
                      })()}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </AnimatePresence>

          {/* Loading indicator is now handled in the messages array via 'initial-loading' message */}


          <div
            aria-hidden="true"
            className="pointer-events-none"
            style={{ height: activeQuestionScrollSpace }}
          />
        </div>
      </div>

      {onSubmit && (
        <ChatInput
          onSubmit={handleComposerSubmit}
          isLoading={isLoading}
          dataSourceStatus={dataSourceStatusState}
        />
      )}
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPanelCollapsed && (
          <motion.button
            type="button"
            key="finance-v2-floating-chat-button"
            initial={{ opacity: 0, scale: 0.86, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.86, y: 12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={() => setIsPanelCollapsed(false)}
            className="fixed bottom-6 right-6 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#6E3450] text-white shadow-[0_6px_12px_rgba(31,26,32,.08),0_24px_48px_-12px_rgba(110,52,80,.28)] transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#6E3450]/35 focus:ring-offset-2"
            title="Open chat"
            aria-label="Open chat"
          >
            <ChartColumnIncreasing className="h-7 w-7" />
            <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-white px-1 text-[11px] font-semibold text-[#6E3450] shadow-sm">
              {conversationTurnCount}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Feedback Modal — when the target message already has feedback we
          show the persisted blob in read-only mode. Feedback is write-once
          on chat-v2 (backend rejects re-submits with 409 so the local JSONB
          stays in sync with the Langfuse score record). */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackTargetMessageId(null);
          feedbackSubmittedRef.current = false;
        }}
        onSubmit={handleFeedbackSubmit}
        isLoading={feedbackLoading}
        title={targetFeedback ? 'Your submitted feedback' : 'Rate your experience'}
        subtitle={
          targetFeedback
            ? 'You already submitted feedback for this response. You can review it below.'
            : 'Your feedback is final once submitted.'
        }
        placeholder="Add a comment (required)"
        initialRating={
          targetFeedback?.rating === 'thumbs_up'
            ? 5
            : targetFeedback?.rating === 'thumbs_down'
              ? 1
              : 0
        }
        initialFeedback={targetFeedback?.text ?? ''}
        readOnly={!!targetFeedback}
        submittedAt={targetFeedback?.submitted_at ?? null}
      />

      {!isPanelCollapsed && showQuestionJumpButton && latestQuestionJumpTarget && (
        // Anchored a little above the ChatInput composer so it never
        // overlaps the Effort (Normal / Deep) toggle that lives in the
        // composer's footer row. ChatInput height when the textarea is
        // empty: pt-2 (8) + composer (~50) + mt-1.5 (6) + footer row
        // (~22) + pb-3 (12) ≈ 98px; bottom-[112px] clears it with a
        // small breathing gap so the button sits just above the textbox.
        <button
          type="button"
          onClick={() => jumpToQuestion(latestQuestionJumpTarget.questionId, latestQuestionJumpTarget.answerId)}
          className="absolute bottom-[112px] right-4 z-20 inline-flex items-center gap-2 rounded-[8px] border border-[#6E3450]/20 bg-white px-3 py-2 text-sm font-medium text-[#6E3450] shadow-lg transition-all duration-200 hover:bg-[#f5e9ee]/60"
          title={latestQuestionJumpTarget.fullText}
          aria-label="Scroll to question"
        >
          <ArrowUp className="h-4 w-4" />
          Question
        </button>
      )}

    </div>
  );
}
